import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AuthModalProvider } from "@/context/AuthModalContext";
import { PhaseHSuperAdminProvider } from "@/context/PhaseHSuperAdminContext";
import AuthModal from "@/components/auth/AuthModal";
import AuthCallbackHandler from "@/components/auth/AuthCallbackHandler";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import LoginRoute from "@/pages/auth/LoginRoute";
// F10 Phase 0 · lazy-loaded admin page to keep main bundle under 850 kB
const AdminPipelinePage = lazy(() => import("@/pages/admin/pipeline/AdminPipelinePage"));
// Hotfix FASE 2 · V12 Achromatic Strict · Admin Hub (launcher ~190 route canoniche).
const AdminHubPage = lazy(() => import("@/pages/admin/AdminHubPage"));
// Hotfix FASE B · V12 Achromatic Strict · User Hub (launcher ~150 route user canoniche).
const UserHubPage = lazy(() => import("@/pages/v12/UserHubPage"));
// Phase IX.G · Admin Monitoring Dashboard (BackupEngine + ThreatDetection + Scalability + DocsEngine).
const AdminMonitoringDashboard = lazy(() => import("@/pages/admin/MonitoringDashboard"));
const AdminEnginesHealthPage   = lazy(() => import("@/pages/admin/EnginesHealthPage"));
// Super Admin Hub · /admin/super · launcher super_admin-only (Phase closure).
const SuperAdminHub = lazy(() => import("@/pages/admin/SuperAdminHub"));
// P0 Pre-launch Pricing · FASE C · super-admin pricing editor (plans/bundles/addons/costs).
const AdminPricingEditor = lazy(() => import("@/pages/admin/PricingEditor"));
// P0 Pre-launch Pricing · FASE F · analytics avanzata (Scale+ gated).
const AnalyticsAdvancedPage = lazy(() => import("@/pages/analytics/AnalyticsAdvancedPage"));
// P0 Pre-launch Pricing · FASE G · automations builder (Growth+ gated, CRUD only).
const AutomationsBuilderPage = lazy(() => import("@/pages/automations/AutomationsBuilderPage"));
// F13.5 · P1 · lazy-loaded admin pages (contact/waitlist/email-queue)
const AdminContactSubmissionsPage = lazy(() => import("@/pages/admin/ContactSubmissionsPage"));
const AdminWaitlistPage = lazy(() => import("@/pages/admin/WaitlistPage"));
const AdminEmailQueuePage = lazy(() => import("@/pages/admin/EmailQueuePage"));
// M14 · Blog Engine · Phase A · lazy-loaded admin pages (zero-touch legacy blog)
const BlogM14ArticlesListPage = lazy(() => import("@/pages/admin/blog_m14/ArticlesListPage"));
const BlogM14ArticleNewPage = lazy(() => import("@/pages/admin/blog_m14/ArticleNewPage"));
const BlogM14TopicSuggestionsPage = lazy(() => import("@/pages/admin/blog_m14/TopicSuggestionsPage"));
const BlogM14ArticleDetailPage = lazy(() => import("@/pages/admin/blog_m14/ArticleDetailPage"));
// M14 · Blog Engine · Phase D · Distribution Integration (additive, zero-touch Phase A/B/C)
const BlogM14DistributionPartnersPage = lazy(() => import("@/pages/admin/blog_m14/distribution/PartnersPage"));
const BlogM14DistributionJobsPage = lazy(() => import("@/pages/admin/blog_m14/distribution/JobsPage"));
// M14 · Blog Engine · Phase E · Analytics Engine (additive, zero-touch Phase A/B/C/D)
const BlogM14AnalyticsDashboardPage = lazy(() => import("@/pages/admin/blog_m14/analytics/AnalyticsDashboardPage"));
// M14 · Blog Engine · Phase F · Recycling Engine (additive, zero-touch Phase A-E)
const BlogM14RecyclingPolicyPage = lazy(() => import("@/pages/admin/blog_m14/recycling/PolicyPage"));
const BlogM14RecyclingQueuePage = lazy(() => import("@/pages/admin/blog_m14/recycling/QueuePage"));
const BlogM14RecyclingSuggestionsPage = lazy(() => import("@/pages/admin/blog_m14/recycling/SuggestionsPage"));
// M14 · Blog Engine · Phase G Wave 4 · SERP + Social admin UI (additive, zero-touch sealed)
const BlogM14SerpDashboardPage = lazy(() => import("@/pages/admin/blog_m14/phase_g/SerpDashboardPage"));
const BlogM14SerpSitesPage = lazy(() => import("@/pages/admin/blog_m14/phase_g/SerpSitesPage"));
const BlogM14SocialCredentialsPage = lazy(() => import("@/pages/admin/blog_m14/phase_g/SocialCredentialsPage"));
const BlogM14SocialDispatchLogPage = lazy(() => import("@/pages/admin/blog_m14/phase_g/SocialDispatchLogPage"));
// M14 · Blog Engine · Phase H Wave 1 · admin UI (additive, zero-touch Phase G sealed)
const BlogM14PhaseHSerpDashboard = lazy(() => import("@/pages/admin/blog_m14/phase_h/SerpDashboardPageH"));
const BlogM14PhaseHSocialInsights = lazy(() => import("@/pages/admin/blog_m14/phase_h/SocialInsightsPage"));
const BlogM14PhaseHSocialBatchRetry = lazy(() => import("@/pages/admin/blog_m14/phase_h/SocialBatchRetryPage"));
// M14 · Blog Engine · Phase H Wave 2B · V12 Admin Dashboard parallel layout (additive)
const V12AdminDashboardPage = lazy(() => import("@/pages/admin/v12/V12AdminDashboardPage"));
// M14 · Blog Engine · Phase H Wave 3A · SSE realtime dispatch log live (additive)
const V12SSEDispatchLogPage = lazy(() => import("@/pages/admin/v12/wave3/SSEDispatchLogPage"));
// M14 · Blog Engine · Phase H Wave 3B.1 · Thread Twitter / Carousel LinkedIn (additive · mock)
const V12ThreadAdminPage = lazy(() => import("@/pages/admin/v12/wave3/ThreadAdminPage"));
// M14 · Blog Engine · Phase H Wave 3B.2 · Media upload social (additive · pod-local storage)
const V12MediaAdminPage = lazy(() => import("@/pages/admin/v12/wave3/MediaAdminPage"));
// M14 · Blog Engine · Phase H+1 Mini-Wave 5 · User Dashboard V12 (additive · parallel shell)
const V12UserDashboardPage = lazy(() => import("@/pages/v12/UserDashboardPage"));
// Phase I Wave 2.3 (F81) · Full referral dashboard page.
const V2ReferralPage = lazy(() => import("@/pages/v2/ReferralPage"));
// Phase I Wave 2.4 · Admin Dev Toolkit (super_admin only, Preview-gated).
const V2AdminDevToolkitPage = lazy(() => import("@/pages/v2/AdminDevToolkitPage"));
// Phase I Wave 3.1 · Social Integrations · B2B Lead Discovery + Admin status page.
const V2LeadDiscoveryPage = lazy(() => import("@/pages/v2/LeadDiscoveryPage"));
const V2AdminIntegrationsPage = lazy(() => import("@/pages/v2/AdminIntegrationsPage"));
// Phase I Wave 3.2 · Content Discovery (YouTube LIVE + Medium/Substack MOCK).
const V2ContentDiscoveryPage = lazy(() => import("@/pages/v2/ContentDiscoveryPage"));
// Phase I Wave 3.3 · B2C Social Discovery (Twitter/Facebook/Instagram/TikTok/Quora).
const V2B2CDiscoveryPage = lazy(() => import("@/pages/v2/B2CDiscoveryPage"));
import ImpersonationBanner from "@/components/ImpersonationBanner";
// Phase I Wave 2.4 R2 · super_admin-only guard reused from Wave 1.11 (zero-touch).
import V12SuperAdminOnlyRoute from "@/components/user_v12/V12SuperAdminOnlyRoute";

// Phase VI.5 · Wave C · V12 Super Admin shell · lazy imports.
const V12SuperAdminDashboardPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminDashboardPage"));
const V12SuperAdminTenantsListPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminTenantsListPage"));
const V12SuperAdminTenantDetailPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminTenantDetailPage"));
const V12SuperAdminEnterprisePage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminClusterPages").then((m) => ({ default: m.V12SuperAdminEnterprisePage })));
const V12SuperAdminResellersPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminClusterPages").then((m) => ({ default: m.V12SuperAdminResellersPage })));
const V12SuperAdminAnalyticsPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminClusterPages").then((m) => ({ default: m.V12SuperAdminAnalyticsPage })));
const V12SuperAdminBrandingPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminClusterPages").then((m) => ({ default: m.V12SuperAdminBrandingPage })));
const V12SuperAdminAlertsPage = React.lazy(() => import("@/pages/super_admin/v12/V12SuperAdminClusterPages").then((m) => ({ default: m.V12SuperAdminAlertsPage })));

// Phase II · Wave A.3 · Universe Hub (Agent registry · Task graph builder · Message bus).
// All five pages are additive · lazy-loaded · ProtectedRoute-gated.
const UniverseHubDashboardPage = lazy(() => import("@/pages/v7/universe/UniverseHubDashboardPage"));
const UniverseAgentRegistryPage = lazy(() => import("@/pages/v7/universe/AgentRegistryPage"));
const UniverseTaskGraphListPage = lazy(() => import("@/pages/v7/universe/TaskGraphListPage"));
const UniverseTaskGraphBuilderPage = lazy(() => import("@/pages/v7/universe/TaskGraphBuilderPage"));
const UniverseMessageInboxPage = lazy(() => import("@/pages/v7/universe/MessageInboxPage"));

// Phase II · Wave B.4 · Marketplace Storefront page bundles.
// All pages additive · lazy-loaded. Browse/detail pages are PUBLIC
// (no ProtectedRoute); seller/buyer dashboards require auth; admin
// moderation requires `adminOnly`. These routes coexist with the
// legacy `/marketplace/*` routes (Phase 24) which stay untouched.
const MarketplaceStorefrontPageB4    = lazy(() => import("@/pages/v8/marketplace/MarketplaceStorefrontPage"));
const MarketplaceListingDetailPageB4 = lazy(() => import("@/pages/v8/marketplace/ListingDetailPage"));
const MarketplaceSellerProfilePageB4 = lazy(() => import("@/pages/v8/marketplace/SellerProfilePage"));
const MarketplaceMyListingsPageB4    = lazy(() => import("@/pages/v8/marketplace/MyListingsPage"));
const MarketplaceCreateListingPageB4 = lazy(() => import("@/pages/v8/marketplace/CreateListingPage"));
const MarketplaceMyPurchasesPageB4   = lazy(() => import("@/pages/v8/marketplace/MyPurchasesPage"));
const MarketplaceAdminModerationPageB4 = lazy(() => import("@/pages/v8/marketplace/AdminModerationPage"));

// Phase II · Wave C.5a · Admin Blog Workbench · 4 pages · all lazy-loaded.
// Gated by EditorialRouteGuard which calls /api/v9/blog/editorial-roles/me.
// Admin-only pages (EditorialRolesManagerPage) pass `adminOnly` to the guard.
const BlogWorkbenchDashboardPage = lazy(() => import("@/pages/v9/admin/BlogWorkbenchDashboardPage"));
const PostComposerPage           = lazy(() => import("@/pages/v9/admin/PostComposerPage"));
const ModerationQueuePage        = lazy(() => import("@/pages/v9/admin/ModerationQueuePage"));
const EditorialRolesManagerPage  = lazy(() => import("@/pages/v9/admin/EditorialRolesManagerPage"));
// Phase II · Wave C.5b · Admin Observability Dashboards (additive).
const CalendarPage               = lazy(() => import("@/pages/v9/admin/CalendarPage"));
const ClustersPage               = lazy(() => import("@/pages/v9/admin/ClustersPage"));
const SitemapStatsPage           = lazy(() => import("@/pages/v9/admin/SitemapStatsPage"));
const ContentGenJobsPage         = lazy(() => import("@/pages/v9/admin/ContentGenJobsPage"));
import EditorialRouteGuard       from "@/components/v9/admin/EditorialRouteGuard";
// Phase I Wave 2.5 R2 · GatedRoute wraps ProtectedRoute + FeatureGate for
// route-level plan-gating · 3 user-facing demo routes mounted below.
import GatedRoute from "@/components/GatedRoute";
const V2SeoMasterDashboardShell = lazy(() =>
    import("@/pages/v2/V2GatedFeatureShell").then((m) => ({ default: m.SeoMasterDashboardShell })),
);
const V2BlogEngineShell = lazy(() =>
    import("@/pages/v2/V2GatedFeatureShell").then((m) => ({ default: m.BlogEngineShell })),
);
const V2AIAgentsShell = lazy(() =>
    import("@/pages/v2/V2GatedFeatureShell").then((m) => ({ default: m.AIAgentsShell })),
);
// Phase I Wave 1.5 · Twitter/LinkedIn placeholder shells preserved (Phase I Wave 2 target).
const V12TwitterIntegrationShell = lazy(() => import("@/pages/v12/placeholders/TwitterIntegrationShell"));
const V12LinkedInIntegrationShell = lazy(() => import("@/pages/v12/placeholders/LinkedInIntegrationShell"));
// Phase I Wave 1.10 · Enterprise Contact Form + admin requests list (additive).
const V12EnterpriseContactPage = lazy(() => import("@/pages/v12/contact/EnterpriseContactPage"));
const V12EnterpriseThankYouPage = lazy(() => import("@/pages/v12/contact/EnterpriseThankYouPage"));
const V12AdminEnterpriseRequestsPage = lazy(() => import("@/pages/admin/v12/EnterpriseRequestsPage"));
// Phase I Wave 1.11 · Admin Analytics Tenants (super_admin · aggregated).
const V12AdminAnalyticsTenantsPage = lazy(() => import("@/pages/admin/v12/AnalyticsTenantsPage"));
// Phase I Wave 1 · Go Live Prep · Admin Live Mode flip panel (super_admin only).
const V12AdminLiveModePage = lazy(() => import("@/pages/admin/v12/LiveModePage"));
// Phase I Wave 2 · Social & Distribution admin pages (Reddit, Medium, Quora, Substack).
const V12AdminSocialRedditPage = lazy(() => import("@/pages/admin/v12/social/RedditPage"));
const V12AdminSocialMediumPage = lazy(() => import("@/pages/admin/v12/social/MediumPage"));
const V12AdminSocialQuoraPage = lazy(() => import("@/pages/admin/v12/social/QuoraPage"));
const V12AdminSocialSubstackPage = lazy(() => import("@/pages/admin/v12/social/SubstackPage"));
// M14 · Phase I Wave 1.5 · V12 User Frontend Wiring (billing + integrations real)
const V12CreditPackPurchasePage = lazy(() => import("@/pages/v12/billing/CreditPackPurchasePage"));
const V12SubscriptionPage = lazy(() => import("@/pages/v12/billing/SubscriptionPage"));
// Phase I Wave 1.21 (F84) · Unified subscription + pricing surface.
// Public-ish wrapper that picks either <PublicPricingShell /> (no auth)
// or <V12SubscriptionPage /> (authenticated) based on AuthContext.
const UnifiedSubscriptionPage = lazy(() => import("@/pages/v12/billing/UnifiedSubscriptionPage"));
// Phase I Wave 1.22 (F85) · one component serves ~55 placeholder routes
// under `/v2/*`. Routes are registered via a `.map()` over the
// registry so adding a new placeholder is a one-line entry.
const V12ComingSoonPage = lazy(() => import("@/pages/v12/placeholders/V12ComingSoonPage"));
import { listComingSoonEntries as _f85_listComingSoonEntries } from "@/data/v12_coming_soon_registry";

// Phase VI.5 Wave A · R2 Issue 3/4 · V12 Workspace shell wrapper
// imported sync (used by all 11 Wave A aliases below). Zero-Touch:
// the underlying page components (Blog, Marketplace, Affiliate, …) stay
// untouched; their internal public chrome (HeaderV4/FooterV4) no-ops on
// /v2/* URLs (see components/header-v4/HeaderV4.jsx + components/footer-v4/FooterV4.jsx).
import V12UserLayout from "@/components/user_v12/V12UserLayout";
// Phase VI.5 Wave B · V12 Admin shell wrapper used by the 24 Wave B
// admin aliases below. Lazy-loaded (large component bundle).
const V12AdminLayout = lazy(() => import("@/components/admin_v12/V12AdminLayout"));

// Phase I Wave 1.23 (F86) · CRM cluster · 6 real pages wired to
// /api/crm/*. Pages are lazy to keep the main chunk slim; the backend
// router auto-seeds deterministic mock data on first request.
const CRMContactsPage  = lazy(() => import("@/pages/v12/crm/CRMContactsPage"));
const CRMCompaniesPage = lazy(() => import("@/pages/v12/crm/CRMCompaniesPage"));
const CRMDealsPage     = lazy(() => import("@/pages/v12/crm/CRMDealsPage"));
const CRMPipelinePage  = lazy(() => import("@/pages/v12/crm/CRMPipelinePage"));
const CRMRoutingPage   = lazy(() => import("@/pages/v12/crm/CRMRoutingPage"));
const CRMAgentsPage    = lazy(() => import("@/pages/v12/crm/CRMAgentsPage"));

// Phase I Wave 1.25 (F88) · Account cluster · 7 real pages wired to
// /api/account/*. Prezzi redirects to /v2/subscription (F84).
const AccountProfilePage         = lazy(() => import("@/pages/v12/account/AccountProfilePage"));
const AccountCreditsPage         = lazy(() => import("@/pages/v12/account/AccountCreditsPage"));
const AccountCreditsHistoryPage  = lazy(() => import("@/pages/v12/account/AccountCreditsHistoryPage"));
const AccountBillingPage         = lazy(() => import("@/pages/v12/account/AccountBillingPage"));
const AccountEmailPage           = lazy(() => import("@/pages/v12/account/AccountEmailPage"));
const AccountPrivacyPage         = lazy(() => import("@/pages/v12/account/AccountPrivacyPage"));
const AccountAPIKeysPage         = lazy(() => import("@/pages/v12/account/AccountAPIKeysPage"));
const AccountSecurityPage        = lazy(() => import("@/pages/v12/account/AccountSecurityPage"));

// Phase I Wave 6.1 · GDPR / CCPA Data Rights Automation · mounted on
// a NEW route /account/privacy (additive, zero-touch sealed F88 page
// at /v2/account/privacy).
const V6PrivacyCenterPage        = lazy(() => import("@/pages/v6/PrivacyCenterPage"));

// Phase I Wave 6.2 · Security Dashboard · admin-only · mounted on
// a NEW route /admin/security (additive, zero-touch sealed admin
// pages).
const V6SecurityDashboardPage    = lazy(() => import("@/pages/v6/SecurityDashboardPage"));

// Phase I Wave 6.3 · Observability Dashboard · admin-only · mounted on
// a NEW route /admin/observability (additive, zero-touch).
const V6ObservabilityDashboardPage = lazy(() => import("@/pages/v6/ObservabilityDashboardPage"));

// Phase I Wave 6.4 · Audit Log admin page (unified privacy + security
// audit log) · mounted on NEW route /admin/audit-log (additive).
const V6AuditLogPage = lazy(() => import("@/pages/v6/AuditLogPage"));

// Phase II Wave D.2 · i18n Strings Workbench · admin/editor review tool.
const I18nStringsWorkbenchPage = lazy(() => import("@/pages/v9/admin/I18nStringsWorkbenchPage"));
const BlogI18nWorkbenchPage = lazy(() => import("@/pages/v9/admin/BlogI18nWorkbenchPage"));
const MarketplaceI18nWorkbenchPage = lazy(() => import("@/pages/v9/admin/MarketplaceI18nWorkbenchPage"));
const BannerAnalyticsPage       = lazy(() => import("@/pages/v9/admin/BannerAnalyticsPage"));

// Phase II · Wave E.1b · Multi-currency transactional checkout + admin.
const CheckoutMultiCurrencyPage = lazy(() => import("@/pages/checkout/CheckoutMultiCurrencyPage"));
const CheckoutSuccessPage        = lazy(() => import("@/pages/checkout/CheckoutSuccessPage"));
const CheckoutFailedPage         = lazy(() => import("@/pages/checkout/CheckoutFailedPage"));
// FASE P1 · Stripe Embedded Checkout + /checkout/return outcome polling.
const EmbeddedCheckoutPage       = lazy(() => import("@/pages/checkout/EmbeddedCheckoutPage"));
const CheckoutReturnPage         = lazy(() => import("@/pages/checkout/CheckoutReturnPage"));
const MultiCurrencyDashboardPage = lazy(() => import("@/pages/v9/admin/MultiCurrencyDashboardPage"));
// Phase II · Wave E.1d · TD7a · US tax-exemption admin review workbench.
const ExemptionReviewPage       = lazy(() => import("@/pages/v9/admin/ExemptionReviewPage"));
// Phase II · Wave E.1e · TD8 · EU OSS compliance workbench (admin + seller).
const OssWorkbenchPage          = lazy(() => import("@/pages/v9/admin/OssWorkbenchPage"));
// Phase III · Wave A · Lead Engine Foundation · 5 admin workbenches.
const LeadsListPage              = lazy(() => import("@/pages/v9/admin/LeadsListPage"));
const LeadDetailPage             = lazy(() => import("@/pages/v9/admin/LeadDetailPage"));
// Phase III · Wave B · Lead Intelligence · additive admin pages.
const LeadsBehaviorAnalyticsPage = lazy(() => import("@/pages/v9/admin/LeadsBehaviorAnalyticsPage"));
const LeadsMlScoringPage         = lazy(() => import("@/pages/v9/admin/LeadsMlScoringPage"));
// Phase III · Wave C · Outreach Automation · additive admin pages.
// Note: v12/outreach already exports symbols with the same name — the v9/admin
// variants are suffixed with `Admin` to avoid a top-level identifier collision
// (hoisted module-scope `const` reuse breaks the Babel parse of the whole file).
const OutreachSequencesAdminPage         = lazy(() => import("@/pages/v9/admin/OutreachSequencesPage"));
const OutreachSequenceBuilderPage        = lazy(() => import("@/pages/v9/admin/OutreachSequenceBuilderPage"));
const OutreachTemplatesAdminPage         = lazy(() => import("@/pages/v9/admin/OutreachTemplatesPage"));
const OutreachTemplateEditorPage         = lazy(() => import("@/pages/v9/admin/OutreachTemplateEditorPage"));
const OutreachEnrollmentsPage            = lazy(() => import("@/pages/v9/admin/OutreachEnrollmentsPage"));
const OutreachAnalyticsPage              = lazy(() => import("@/pages/v9/admin/OutreachAnalyticsPage"));
// Phase III · Wave D · Pipeline + Sales Automation · additive admin pages.
const PipelineKanbanPage            = lazy(() => import("@/pages/v9/admin/PipelineKanbanPage"));
const DealDetailPage                = lazy(() => import("@/pages/v9/admin/DealDetailPage"));
const PipelineConfigPage            = lazy(() => import("@/pages/v9/admin/PipelineConfigPage"));
const RevenueAnalyticsPage          = lazy(() => import("@/pages/v9/admin/RevenueAnalyticsPage"));
// Phase III · Wave E · CRM Core · additive admin pages + layout.
const AccountsListPage              = lazy(() => import("@/pages/v9/admin/AccountsListPage"));
const AccountDetailPage             = lazy(() => import("@/pages/v9/admin/AccountDetailPage"));
const ContactsListPage              = lazy(() => import("@/pages/v9/admin/ContactsListPage"));
const ContactDetailPage             = lazy(() => import("@/pages/v9/admin/ContactDetailPage"));
const CrmAnalyticsPage              = lazy(() => import("@/pages/v9/admin/CrmAnalyticsPage"));
const CrmLayout                     = lazy(() => import("@/components/crm/CrmLayout"));
// Phase III · Wave F · Data/API Services · 4 admin pages + public landing.
const ApiKeysPage                   = lazy(() => import("@/pages/v9/admin/ApiKeysPage"));
const WebhooksPage                  = lazy(() => import("@/pages/v9/admin/WebhooksPage"));
const WebhookHealthPage             = lazy(() => import("@/pages/v9/admin/WebhookHealthPage"));
const DevelopersPage                = lazy(() => import("@/pages/public/DevelopersPage"));
// Phase III · Wave G · Psychological No-Escape Funnel · 6 admin pages (lazy).
const FunnelDisclosurePage      = lazy(() => import("@/pages/v9/admin/FunnelDisclosurePage"));
const FunnelExitIntentPage      = lazy(() => import("@/pages/v9/admin/FunnelExitIntentPage"));
const FunnelGamificationPage    = lazy(() => import("@/pages/v9/admin/FunnelGamificationPage"));
const FunnelSocialProofPage     = lazy(() => import("@/pages/v9/admin/FunnelSocialProofPage"));
const FunnelUrgencyPage         = lazy(() => import("@/pages/v9/admin/FunnelUrgencyPage"));
const FunnelAnalyticsPage       = lazy(() => import("@/pages/v9/admin/FunnelAnalyticsPage"));
// Phase III · Wave H · A/B Optimizer polish · additive · plan-gated Scale+.
const AbOptimizerPage           = lazy(() => import("@/pages/v9/admin/AbOptimizerPage"));
// Phase IV · Wave A · Webhooks v2 · retry + DLQ + HMAC rotation + filter DSL.
const WebhooksV2Page            = lazy(() => import("@/pages/v9/admin/developers/WebhooksV2"));
const WebhooksV2DlqPage         = lazy(() => import("@/pages/v9/admin/developers/WebhooksV2DLQ"));
const WebhooksV2RegistryPage    = lazy(() => import("@/pages/v9/admin/developers/WebhooksV2Registry"));
const WebhooksV2DetailPage      = lazy(() => import("@/pages/v9/admin/developers/WebhooksV2SubscriptionDetail"));
// Phase IV · Wave B · Marketplace preset integrations.
const IntegrationsPage          = lazy(() => import("@/pages/v9/admin/marketplace/Integrations"));
const IntegrationDetailPage     = lazy(() => import("@/pages/v9/admin/marketplace/IntegrationDetail"));
const MyInstallationsPage       = lazy(() => import("@/pages/v9/admin/marketplace/MyInstallations"));
// Phase IV · Wave C · Seller onboarding guided wizard.
const SellerOnboardingPage      = lazy(() => import("@/pages/v9/admin/marketplace/SellerOnboarding"));
const SellerProfilePage         = lazy(() => import("@/pages/v9/admin/marketplace/SellerProfile"));
const AdminSellerReviewPage     = lazy(() => import("@/pages/v9/admin/marketplace/AdminSellerReview"));
// Phase IV · Wave D · AI Agent marketplace extension (templates + royalties).
const AgentCatalogPage          = lazy(() => import("@/pages/v9/admin/marketplace/agents/AgentCatalog"));
const AgentTemplateDetailPage   = lazy(() => import("@/pages/v9/admin/marketplace/agents/AgentTemplateDetail"));
const CreatorStudioPage         = lazy(() => import("@/pages/v9/admin/marketplace/agents/CreatorStudio"));
const CreatorTemplateEditorPage = lazy(() => import("@/pages/v9/admin/marketplace/agents/CreatorTemplateEditor"));
const CreatorRoyaltiesPage      = lazy(() => import("@/pages/v9/admin/marketplace/agents/CreatorRoyalties"));
const AdminAgentReviewPage      = lazy(() => import("@/pages/v9/admin/marketplace/agents/AdminAgentReview"));
// Phase IV · Wave E · Federated Search · Portal-based ⌘K bar + 2 pages.
const FederatedSearchBar        = lazy(() => import("@/components/search/FederatedSearchBar"));
const SearchResultsPage         = lazy(() => import("@/pages/v9/admin/search/SearchResults"));
const AdminSearchStatusPage     = lazy(() => import("@/pages/v9/admin/search/AdminSearchStatus"));
// Phase IV · Wave F · Advanced Analytics Dashboard (4-tab hub).
const AdvancedAnalyticsPage     = lazy(() => import("@/pages/v9/admin/analytics/AdvancedDashboard"));
// Phase IV · Wave G · Enterprise RBAC/SSO · 4 admin pages.
const EnterpriseHomePage        = lazy(() => import("@/pages/v9/admin/enterprise/EnterpriseHome"));
const EnterpriseTenantsPage     = lazy(() => import("@/pages/v9/admin/enterprise/TenantsAdmin"));
const EnterpriseSsoConfigsPage  = lazy(() => import("@/pages/v9/admin/enterprise/SsoConfigs"));
const EnterpriseRolesPage       = lazy(() => import("@/pages/v9/admin/enterprise/RolesMatrix"));
const EnterpriseUsersPage       = lazy(() => import("@/pages/v9/admin/enterprise/UsersPermissions"));
// Phase V · Wave A · RBAC legacy enforcement · audit denials viewer.
const EnterpriseAuditPage       = lazy(() => import("@/pages/v9/admin/enterprise/AuditDenialsPage"));
const MigrationHardCutoverPage  = lazy(() => import("@/pages/v9/admin/enterprise/MigrationHardCutover"));
// Phase V · Wave C · Reseller mode + pricing tier · adminOnly / reseller.
const ResellerDashboardPage     = lazy(() => import("@/pages/v9/admin/reseller/ResellerDashboard"));
const ResellerSubtenantsPage    = lazy(() => import("@/pages/v9/admin/reseller/ResellerSubtenants"));
const ResellerCommissionsPage   = lazy(() => import("@/pages/v9/admin/reseller/ResellerCommissions"));
// Phase V · Wave D · Branding + custom domains · adminOnly.
const BrandingEditorPage        = lazy(() => import("@/pages/v9/admin/branding/BrandingEditor"));
const BrandingCustomDomainsPage = lazy(() => import("@/pages/v9/admin/branding/CustomDomains"));
// Phase VII.H.B · V12 Admin custom domains with real DNS + TLS flow.
const AdminCustomDomainsPageHB  = lazy(() => import("@/pages/v12/admin/AdminCustomDomainsPageHB"));
// Phase VI · Wave A · Blog engine.
const BlogListPage              = lazy(() => import("@/pages/v9/admin/blog/BlogList"));
const BlogEditorPage            = lazy(() => import("@/pages/v9/admin/blog/BlogEditor"));
const PublicBlogIndexPage       = lazy(() => import("@/pages/public/blog/BlogIndex"));
const PublicBlogPostPage        = lazy(() => import("@/pages/public/blog/BlogPost"));
// Phase VI · Wave B · SEO Master System · admin + public (lazy).
const SeoKeywordsPage           = lazy(() => import("@/pages/v9/admin/seo/KeywordsList"));
const SeoTopicsQueuePage        = lazy(() => import("@/pages/v9/admin/seo/TopicsQueue"));
const SeoPagesListPage          = lazy(() => import("@/pages/v9/admin/seo/PagesList"));
const SeoPageEditorPage         = lazy(() => import("@/pages/v9/admin/seo/PageEditor"));
const SeoPublicPage             = lazy(() => import("@/pages/public/seo/SeoPublicPage"));
// Phase VII · Wave SEO.AUTO · V12 admin SEO pages (indexing · GSC · Bing).
// Named lazy exports via a tiny thunk helper (avoids babel JSX edge case
// con `.then()` inline nel return del dynamic import).
const _lazyNamed = (loader, name) => lazy(async () => {
    const mod = await loader();
    return { default: mod[name] };
});
const V12AdminSEOIndexingPage = _lazyNamed(
    () => import("@/pages/admin/seo/V12AdminSEOPages"), "V12AdminSEOIndexingPage");
const V12AdminGSCPage = _lazyNamed(
    () => import("@/pages/admin/seo/V12AdminSEOPages"), "V12AdminGSCPage");
const V12AdminBingPage = _lazyNamed(
    () => import("@/pages/admin/seo/V12AdminSEOPages"), "V12AdminBingPage");
// Phase VI · Wave C · Affiliate Program (lazy).
const AffiliateDashboardPage    = lazy(() => import("@/pages/v9/admin/affiliate/AffiliateDashboard"));
const AffiliateMaterialsPage    = lazy(() => import("@/pages/v9/admin/affiliate/AffiliateMaterials"));
const AffiliatePayoutsPage      = lazy(() => import("@/pages/v9/admin/affiliate/AffiliatePayouts"));
const AdminAffiliateReviewPage  = lazy(() => import("@/pages/v9/admin/affiliate/AdminAffiliateReview"));
const AffiliateRedirect         = lazy(() => import("@/pages/public/affiliate/AffiliateRedirect"));
// Phase VI · Wave D · Social Automation (lazy · MOCK).
const SocialConnectionsPage     = lazy(() => import("@/pages/v9/admin/social/SocialConnections"));
const SocialComposerPage        = lazy(() => import("@/pages/v9/admin/social/SocialComposer"));
const SocialCalendarPage        = lazy(() => import("@/pages/v9/admin/social/SocialCalendar"));
const SocialHistoryPage         = lazy(() => import("@/pages/v9/admin/social/SocialHistory"));
// Phase VI · Wave E · Press / Distribution (lazy).
const PressReleasesListPage     = lazy(() => import("@/pages/v9/admin/press/ReleasesList"));
const PressReleaseEditorPage    = lazy(() => import("@/pages/v9/admin/press/ReleaseEditor"));
const PressContactsPage         = lazy(() => import("@/pages/v9/admin/press/PressContacts"));
const PressMentionsPage         = lazy(() => import("@/pages/v9/admin/press/PressMentions"));
const PublicPressRoomPage       = lazy(() => import("@/pages/public/press/PressRoom"));
const PublicPressReleasePage    = lazy(() => import("@/pages/public/press/PressReleasePage"));
const PublicMediaKitPage        = lazy(() => import("@/pages/public/press/MediaKit"));
// Phase VI · Wave F · Referral Viral Loop (closes Phase VI).
const ReferralInvitePage        = lazy(() => import("@/pages/v9/admin/referral/ReferralInvite"));
const ReferralDashboardPage     = lazy(() => import("@/pages/v9/admin/referral/ReferralDashboard"));
const ReferralLeaderboardPage   = lazy(() => import("@/pages/v9/admin/referral/ReferralLeaderboard"));
const ReferralSignupPrefillPage = lazy(() => import("@/pages/public/referral/ReferralSignupPrefill"));
const LeadsCsvImportPage         = lazy(() => import("@/pages/v9/admin/LeadsCsvImportPage"));
const LeadsWebhooksPage          = lazy(() => import("@/pages/v9/admin/LeadsWebhooksPage"));
const LeadsScoringRulesPage      = lazy(() => import("@/pages/v9/admin/LeadsScoringRulesPage"));
// Phase II · Wave E.1c · Rate-limiter observability.
const RateLimiterAdminPage       = lazy(() => import("@/pages/v9/admin/RateLimiterAdminPage"));
// Phase II · Wave E.1c.2 · TD1 · Public compliance VAT page.
const ComplianceVatPage          = lazy(() => import("@/pages/compliance/ComplianceVatPage"));

// Phase I Wave 6.4 · Global cookie-consent banner · rendered OUTSIDE
// <Routes> so it appears on every page until the user picks a
// preference. GDPR-compliant · 3 categories · acromatic palette.
const V6CookieConsentBanner = lazy(() => import("@/components/v6/CookieConsentBanner"));

// Phase I Wave 1.28 (F91) · Lead & Outreach cluster · 6 real pages wired
// to /api/leads/* and /api/outreach/*.
const LeadEnginePage         = lazy(() => import("@/pages/v12/leads/LeadEnginePage"));
const SavedLeadsPage         = lazy(() => import("@/pages/v12/leads/SavedLeadsPage"));
const OutreachOverviewPage   = lazy(() => import("@/pages/v12/outreach/OutreachOverviewPage"));
const OutreachTemplatesPage  = lazy(() => import("@/pages/v12/outreach/TemplatesPage"));
const OutreachSequencesPage  = lazy(() => import("@/pages/v12/outreach/SequencesPage"));
const OutreachCampaignsPage  = lazy(() => import("@/pages/v12/outreach/CampaignsPage"));

// Phase I Wave 1.29 (F92) · AI Suite + PRESS + Academy + Community + Partnership-mini.
const AIUniversePage         = lazy(() => import("@/pages/v12/ai/AIUniversePage"));
const RAGPage                = lazy(() => import("@/pages/v12/ai/RAGPage"));
const LeadEnrichmentPage     = lazy(() => import("@/pages/v12/leads/LeadEnrichmentPage"));
const ApolloLivePage         = lazy(() => import("@/pages/v12/leads/ApolloLivePage"));
const CreatorPublishWorkflowPage = lazy(() => import("@/pages/v12/marketplace/CreatorPublishWorkflowPage"));
const AdminFeeBandsPage      = lazy(() => import("@/pages/v12/admin/AdminFeeBandsPage"));
const AIBadgesPage           = lazy(() => import("@/pages/v12/ai/AIBadgesPage"));
const AILeaderboardPage      = lazy(() => import("@/pages/v12/ai/AILeaderboardPage"));
const AIPromptsPage          = lazy(() => import("@/pages/v12/ai/AIPromptsPage"));
const PressReleasesPage      = lazy(() => import("@/pages/v12/press/PressReleasesPage"));
const PressGeneratorPage     = lazy(() => import("@/pages/v12/press/PressGeneratorPage"));
const PressDistributionPage  = lazy(() => import("@/pages/v12/press/PressDistributionPage"));
const AcademyCoursesPage     = lazy(() => import("@/pages/v12/academy/AcademyCoursesPage"));
const AcademyProgressPage    = lazy(() => import("@/pages/v12/academy/AcademyProgressPage"));
const AcademyCertificatesPage= lazy(() => import("@/pages/v12/academy/AcademyCertificatesPage"));
const CommunityFeedPage      = lazy(() => import("@/pages/v12/community/CommunityFeedPage"));
const CommunityGroupsPage    = lazy(() => import("@/pages/v12/community/CommunityGroupsPage"));
const CommunityEventsPage    = lazy(() => import("@/pages/v12/community/CommunityEventsPage"));
const CommunityMessagesPage  = lazy(() => import("@/pages/v12/community/CommunityMessagesPage"));
const CommunityAIPage        = lazy(() => import("@/pages/v12/community/CommunityAIPage"));
const PartnershipMaterialsPage = lazy(() => import("@/pages/v12/partnership/PartnershipMaterialsPage"));
const PartnershipPayoutPage  = lazy(() => import("@/pages/v12/partnership/PartnershipPayoutPage"));

// Phase I Wave 1.24 (F87) · admin coming soon placeholder shell.
const V12AdminComingSoonPage = lazy(() => import("@/pages/v12/placeholders/V12AdminComingSoonPage"));
import { listAdminComingSoonEntries as _f87_listAdminComingSoonEntries } from "@/data/v12_admin_coming_soon_registry";
// Phase I Wave 1.27 (F90) · SPA bridge · /admin/v2/legacy/:slug → classic routes.
import V12AdminLegacyRedirect from "@/components/admin_v12/V12AdminLegacyRedirect";
const V12CheckoutSuccessPage = lazy(() => import("@/pages/v12/billing/CheckoutSuccessPage"));
const V12CheckoutCancelPage = lazy(() => import("@/pages/v12/billing/CheckoutCancelPage"));
const V12EmailResendPage = lazy(() => import("@/pages/v12/integrations/EmailResendPage"));
const V12GSCConnectPage = lazy(() => import("@/pages/v12/integrations/GSCConnectPage"));
const V12BingDashboardPage = lazy(() => import("@/pages/v12/integrations/BingDashboardPage"));
// POST-FIX role-matrix PRD V10 · route-level guard admin-only
import V12AdminOnlyRoute from "@/components/user_v12/V12AdminOnlyRoute";
// Phase H+1 Mini-Wave 6 · Admin Cockpit Extension · placeholder catch-all
const V12AdminPlaceholderPage = lazy(() => import("@/pages/admin/v12/placeholders/AdminPlaceholderPage"));
import ProtectedRoute from "@/components/ProtectedRoute";
// FASE J · Reusable full-page-reload helper for deprecated SPA landing
// routes (`/`, `/v2`, `/v3`, `/landing-v1`, `/en-US/*`). All of these
// now bounce the browser to the Next.js SSR landing served at `/`.
import ForceReload from "@/components/ForceReload";
import Dashboard from "@/pages/Dashboard";
import DashboardRouter from "@/pages/dashboard-v3/DashboardRouter";
import DashboardV3 from "@/pages/dashboard-v3/DashboardV3";
import Profile from "@/pages/Profile";
import Credits from "@/pages/Credits";
import Leads from "@/pages/Leads";
import SavedLeads from "@/pages/SavedLeads";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutFailed from "@/pages/CheckoutFailed";
import Outreach from "@/pages/Outreach";
import OutreachTemplates from "@/pages/OutreachTemplates";
import OutreachSequences from "@/pages/OutreachSequences";
import OutreachCampaigns from "@/pages/OutreachCampaigns";
import OutreachCampaignNew from "@/pages/OutreachCampaignNew";
import OutreachCampaignDetail from "@/pages/OutreachCampaignDetail";
import Unsubscribe from "@/pages/Unsubscribe";
import Pricing from "@/pages/Pricing";
import PricingV24a from "@/pages/pricing/Pricing";
// Phase I Wave 1.19 (F82) · unified USD V12 pricing page.
// Phase I Wave 1.21 (F84) · DEPRECATED · the /pricing route now
// redirects to /v2/subscription (served by UnifiedSubscriptionPage).
// Component kept imported-free (no route references it) · the file
// itself still lives at `pages/pricing/PricingV12USD.jsx` for audit
// and is renamed `.deprecated.jsx` in a follow-up cleanup pass.
// FASE J · LandingV1/V2/V3 SPA pages deleted. Legacy URLs
// (`/v2`, `/v3`, `/landing-v1`, `/admin/preview/landing-v{1,2,3}`,
// `/en-US`, `/en-US/*`) now redirect via <ForceReload to="/" /> to
// the authoritative Next.js SSR landing.
import CommunityDiscover from "@/pages/community-discover/CommunityDiscover";
import AdminPayments from "@/pages/admin/payments/AdminPayments";
import AdminUpsell from "@/pages/admin/upsell/AdminUpsell";
import AccountSubscriptions from "@/pages/account/AccountSubscriptions";
import AccountBilling from "@/pages/account/AccountBilling";
import AdminSubscriptions from "@/pages/admin/subscriptions/AdminSubscriptions";
// Phase 24d · TicketingEngine
import EventTicketPurchase from "@/pages/tickets/EventTicketPurchase";
import TicketPurchaseSuccess from "@/pages/tickets/TicketPurchaseSuccess";
import AccountTickets from "@/pages/account/AccountTickets";
import AdminTickets from "@/pages/admin/tickets/AdminTickets";
import EventCheckin from "@/pages/host/EventCheckin";
import Billing from "@/pages/Billing";
import Upgrade from "@/pages/Upgrade";
import Affiliate from "@/pages/Affiliate";
import AdminEnterpriseLeads from "@/pages/AdminEnterpriseLeads";
import AdminAffiliatePayouts from "@/pages/AdminAffiliatePayouts";
import AdminEconomy from "@/pages/AdminEconomy";
// Wave A.4 cleanup · legacy Universe / UniverseWorlds routes removed in favour
// of the Phase II Wave A.3 Universe Hub (``/universe/*``).
import Civilization from "@/pages/Civilization";
import CivilizationLeaderboard from "@/pages/CivilizationLeaderboard";
import CivilizationProfile from "@/pages/CivilizationProfile";
import CivilizationBadges from "@/pages/CivilizationBadges";
import AccountSuspended from "@/pages/AccountSuspended";
import Modules from "@/pages/Modules";
import ModuleRun from "@/pages/ModuleRun";
import Marketplace from "@/pages/Marketplace";
import MarketplaceAsset from "@/pages/MarketplaceAsset";
import MarketplaceLibrary from "@/pages/MarketplaceLibrary";
import AdminModules from "@/pages/AdminModules";
import AdminMarketplace from "@/pages/AdminMarketplace";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBlog from "@/pages/AdminBlog";
import AdminBlogEdit from "@/pages/AdminBlogEdit";
import AdminAutonomy from "@/pages/AdminAutonomy";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminV3Funnel from "@/pages/admin/AdminV3Funnel";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminSystem from "@/pages/AdminSystem";
import AdminAffiliate from "@/pages/AdminAffiliate";
import AdminGovernance from "@/pages/AdminGovernance";
// Phase 11 V50 — Self-Optimization
import AdminV50 from "@/pages/AdminV50";
// Phase 12 Email Engine V10
import EmailEngineDashboard from "@/pages/admin/email-engine/EmailEngineDashboard";
// Phase 13 SEO Engine V10
import SeoEngineDashboard from "@/pages/admin/seo-engine/SeoEngineDashboard";
// Phase 14 SEO Apply Layer
import SeoApplyDashboard from "@/pages/admin/seo-apply/SeoApplyDashboard";
// Phase 15 Email Send Layer
import EmailSendDashboard from "@/pages/admin/email-send/EmailSendDashboard";
import Unsubscribed from "@/pages/Unsubscribed";
// Phase 16b Affiliate v2 (wrapper over Phase 3 · does not replace it)
import AffiliateDashboardV2 from "@/pages/affiliate/AffiliateDashboardV2";
import AdminAffiliateV2 from "@/pages/admin/affiliate/AdminAffiliateV2";
// Phase 16c Marketplace v3 (wrapper over Phase 5 · creator ecosystem)
import CreatorDashboardV3 from "@/pages/marketplace/CreatorDashboardV3";
import AdminMarketplaceV3 from "@/pages/admin/marketplace/AdminMarketplaceV3";
// Phase 24e Marketplace V4 (paid assets + revenue split 70/30)
import AssetDetail from "@/pages/marketplace/AssetDetail";
import CreatorStore from "@/pages/marketplace/CreatorStore";
import CreatorEarnings from "@/pages/marketplace/CreatorEarnings";
import AdminMarketplaceV24e from "@/pages/admin/marketplace/AdminMarketplaceV24e";
// Phase 25b Marketplace V2 Discovery + Cart
import MarketplaceHub from "@/pages/marketplace/MarketplaceHub";
// FASE L round-2 · Legacy credits-era CartPage deleted. `/cart` + the
// shortcut `/marketplace/cart` both now render the USD-ready MP1CartPage.
import CartSuccess from "@/pages/marketplace/CartSuccess";
// FASE M · cart multi-asset Stripe success page with polling.
import CartSuccessPage from "@/pages/v12/marketplace/CartSuccessPage";
import { CartProvider } from "@/context/CartContext";
// Phase VII · Wave Marketplace V10 · MP.1 · lazy loaded.
const MP1CatalogPage  = React.lazy(() => import("@/pages/v12/marketplace/CatalogPage"));
const V12UserLayoutOrPublic = React.lazy(() => import("@/components/marketplace/V12UserLayoutOrPublic"));
const MP1WishlistPage = React.lazy(() => import("@/pages/v12/marketplace/WishlistPage"));
const MP1CartPage     = React.lazy(() => import("@/pages/v12/marketplace/CartPage"));
// Phase VII · Wave Marketplace V10 · MP.2 Creator Studio · lazy loaded.
const MP2CreatorOnboardingPage = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorOnboardingPage"));
const MP2CreatorDashboardPage  = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorDashboardPage"));
const MP2CreatorAssetListPage  = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorAssetListPage"));
const MP2CreatorAssetEditorPage = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorAssetEditorPage"));
const MP2CreatorEarningsPage   = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorEarningsPage"));
// Phase VII · Wave Marketplace V10 · MP.3 Admin Moderation · lazy loaded.
const MP3AdminOverviewPage  = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminMarketplaceOverviewPage"));
const MP3ModerationQueuePage = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminModerationQueuePage"));
const MP3AdminAssetsPage    = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminAssetsManagementPage"));
const MP3AdminCreatorsPage  = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminCreatorsManagementPage"));
const MP3AdminAuditPage     = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminModerationAuditPage"));
// Phase VII · Wave Marketplace V10 · MP.4 Asset Types · lazy loaded.
const MP4MyBookingsPage     = React.lazy(() => import("@/pages/v12/marketplace/MyServiceBookingsPage"));
const MP4MyRedemptionsPage  = React.lazy(() => import("@/pages/v12/marketplace/MyTokenRedemptionsPage"));
const MP4CreatorBookingsPage = React.lazy(() => import("@/pages/v12/marketplace/creator/CreatorServiceBookingsPage"));
// Phase VII · Wave Marketplace V10 · MP.5 Final Consolidation · lazy loaded.
const MP5AssetDetailPage    = React.lazy(() => import("@/pages/v12/marketplace/AssetDetailV10Page"));
const MP7HubPage            = React.lazy(() => import("@/pages/v12/marketplace/MarketplaceHubPage"));
// Wave VII.A · Stripe Credit Packs.
const V7ACreditPacksPage    = React.lazy(() => import("@/pages/v12/billing/CreditPacksPage"));
const V7ACheckoutPage       = React.lazy(() => import("@/pages/v12/billing/CheckoutPage"));
const V7ACheckoutResultPages = React.lazy(() => import("@/pages/v12/billing/CheckoutResultPages"));
const V7ASuccessPage        = React.lazy(() => import("@/pages/v12/billing/CheckoutResultPages").then((m) => ({ default: m.CheckoutSuccessPage })));
const V7AFailedPage         = React.lazy(() => import("@/pages/v12/billing/CheckoutResultPages").then((m) => ({ default: m.CheckoutFailedPage })));
// Wave VII.A R3 · Pricing page (credit packs + credit cost table).
const V7APricingPage        = React.lazy(() => import("@/pages/v12/billing/PricingPage"));
// Wave Marketing Parity V4 · 8 PORT marketing pages (V12 Achromatic Strict).
const V12HomePage           = React.lazy(() => import("@/pages/marketing/V12HomePage"));
const V12SuitesPage         = React.lazy(() => import("@/pages/marketing/V12SuitesPages").then((m) => ({ default: m.V12SuitesPage })));
const V12SuiteDetailPage    = React.lazy(() => import("@/pages/marketing/V12SuitesPages").then((m) => ({ default: m.V12SuiteDetailPage })));
const V12SuitesComparePage  = React.lazy(() => import("@/pages/marketing/V12SuitesPages").then((m) => ({ default: m.V12SuitesComparePage })));
const V12ProductLeadEnginePage  = React.lazy(() => import("@/pages/marketing/V12OtherPages").then((m) => ({ default: m.V12ProductLeadEnginePage })));
const V12ProductMarketplacePage = React.lazy(() => import("@/pages/marketing/V12OtherPages").then((m) => ({ default: m.V12ProductMarketplacePage })));
const V12LeaderboardPage    = React.lazy(() => import("@/pages/marketing/V12OtherPages").then((m) => ({ default: m.V12LeaderboardPage })));
const V12StoryDetailPage    = React.lazy(() => import("@/pages/marketing/V12OtherPages").then((m) => ({ default: m.V12StoryDetailPage })));
// Wave Marketing Parity V4 · 7 V12 legal + status pages.
const V12PrivacyPage        = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12PrivacyPage })));
const V12TermsPage          = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12TermsPage })));
const V12CookiesPage        = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12CookiesPage })));
const V12CompliancePage     = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12CompliancePage })));
const V12SlaPage            = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12SlaPage })));
const V12OnboardingPage     = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12OnboardingPage })));
const V12StatusPage         = React.lazy(() => import("@/pages/marketing/V12LegalPages").then((m) => ({ default: m.V12StatusPage })));
// Wave Marketing Parity V4 · Wave 2 · 12 REPLACE marketing pages.
const V12FeaturesPage        = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12FeaturesPage })));
const V12ModulesPage         = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12ModulesPage })));
const V12EcosystemsPage      = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12EcosystemsPage })));
const V12AiUniversePage      = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12AiUniversePage })));
const V12AiCivilizationPage  = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12AiCivilizationPage })));
const V12BadgeSystemPage     = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12BadgeSystemPage })));
const V12DocsPage            = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12DocsPage })));
const V12DocsCreditsPage     = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12DocsCreditsPage })));
const V12DocsLimitsPage      = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12DocsLimitsPage })));
const V12DocsWebhooksPage    = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12DocsWebhooksPage })));
const V12AcademyPage         = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12AcademyPage })));
const V12SeoPage             = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12SeoPage })));
const V12CommunityPage       = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12CommunityPage })));
const V12ContactPage         = React.lazy(() => import("@/pages/marketing/V12Wave2Pages").then((m) => ({ default: m.V12ContactPage })));
// Task A · Upgrade view · in-app subscription upgrade (dynamic from pricing_plans SSOT).
const UpgradeView           = React.lazy(() => import("@/pages/app/UpgradeView"));
// Wave VII.A R5 · My Subscription management page.
const V7AMySubscriptionPage = React.lazy(() => import("@/pages/v12/billing/MySubscriptionPage"));
// Wave VII.B · Admin Email Logs page (Resend transactional).
const V7BAdminEmailLogsPage = React.lazy(() => import("@/pages/v12/admin/AdminEmailLogsPage"));
// Wave VIII.A · Admin AI Engine page (GlobalAIEngine orchestrator).
const V8AAdminAIEnginePage = React.lazy(() => import("@/pages/v12/admin/AdminAIEnginePage"));
const WaveXEBusinessKPIDashboard = React.lazy(() => import("@/pages/v12/admin/BusinessKPIDashboard"));
// Wave VIII.B · Admin Auto-Creation page (AutoCreationEngine LIVE).
const V8BAdminAutoCreationPage = React.lazy(() => import("@/pages/v12/admin/AdminAutoCreationPage"));
// Wave VIII.C · Admin Auto-Expansion page (AutoExpansionEngine LIVE).
const V8CAdminAutoExpansionPage = React.lazy(() => import("@/pages/v12/admin/AdminAutoExpansionPage"));
// Wave VIII.D · Admin Auto-Marketplace page (AutoMarketplaceEngine LIVE).
const V8DAdminAutoMarketplacePage = React.lazy(() => import("@/pages/v12/admin/AdminAutoMarketplacePage"));
// Wave VIII.E · Admin Auto-SEO page (AutoSEOEngine LIVE).
const V8EAdminAutoSEOPage = React.lazy(() => import("@/pages/v12/admin/AdminAutoSEOPage"));
// Wave VIII.F · Admin Auto-Governance page (AutoGovernanceEngine LIVE).
const V8FAdminAutoGovernancePage = React.lazy(() => import("@/pages/v12/admin/AdminAutoGovernancePage"));
// Wave VIII.G · Admin Auto-Routing page (AutoRoutingEngine LIVE).
const V8GAdminAutoRoutingPage = React.lazy(() => import("@/pages/v12/admin/AdminAutoRoutingPage"));
// Wave VIII.H · Admin Auto-Recommendation page (AutoRecommendationEngine LIVE).
const V8HAdminAutoRecommendationPage = React.lazy(() => import("@/pages/v12/admin/AdminAutoRecommendationPage"));
// Wave VIII.I · Admin Onboarding + Support combo (OnboardingEngine + SupportEngine LIVE).
const V8IAdminOnboardingSupportPage = React.lazy(() => import("@/pages/v12/admin/AdminOnboardingSupportPage"));
// Wave VIII.J · Admin Visionary Engines mega-combo (4 engines LIVE · 13/13).
const V8JAdminVisionaryEnginesPage = React.lazy(() => import("@/pages/v12/admin/AdminVisionaryEnginesPage"));
// Wave VIII Frontend Integration · user-facing onboarding wizard page
// (OnboardingEngine LIVE · Wave VIII.I). Zero-touch route under /onboarding.
const V8IOnboardingWizardPage = React.lazy(() => import("@/pages/v12/onboarding/OnboardingWizardPage"));
// Wave IX.A · AI Economy · user reputation page + admin dashboard (LIVE).
const V9AReputationPage = React.lazy(() => import("@/pages/v12/economy/ReputationPage"));
const V9AAdminAIEconomyPage = React.lazy(() => import("@/pages/v12/admin/AdminAIEconomyPage"));
// Wave X.B · Admin Observability dashboard.
const ObservabilityDashboard = React.lazy(() => import("@/pages/v12/admin/ObservabilityDashboard"));
// Wave IX.B · AI Civilization Ops · 4 deployable use cases (sales/content/support/intel).
const V9BAdminCivilizationOpsPage = React.lazy(() => import("@/pages/v12/admin/AdminCivilizationOpsPage"));
// Wave IX.C · Universe + Ecosystem Hub user-facing (3 pages).
const V9CUniverseHubPage = React.lazy(() => import("@/pages/v12/universe/UniverseHubPage"));
const V9CUniverseInstancePage = React.lazy(() => import("@/pages/v12/universe/UniverseInstancePage"));
const V9CEcosystemHubPage = React.lazy(() => import("@/pages/v12/ecosystem/EcosystemHubPage"));
// Wave IX.D · Automations Hub central + Workflow builder.
const V9DAutomationsHubPage = React.lazy(() => import("@/pages/v12/automations/AutomationsHubPage"));
const V9DWorkflowBuilderPage = React.lazy(() => import("@/pages/v12/automations/WorkflowBuilderPage"));
// Wave IX.E · Ranking Hub + Public user profile.
const V9ERankingHubPage = React.lazy(() => import("@/pages/v12/ranking/RankingHubPage"));
const V9EPublicProfilePage = React.lazy(() => import("@/pages/v12/profile/PublicUserProfilePage"));
// Phase IX.F.2 · Suites v10 (9 preconfigured marketplace bundles).
const V9F2SuitesListPage = React.lazy(() => import("@/pages/v12/suites/SuitesListPage"));
const V9F2SuiteDetailPage = React.lazy(() => import("@/pages/v12/suites/SuiteDetailPage"));
const V9F2SuitesOwnedPage = React.lazy(() => import("@/pages/v12/suites/SuitesOwnedPage"));
// Phase IX.F.4 · PRD V10 Coverage · 3 nuove user pages + 1 admin placeholder.
const V9F4UserModulesPage = React.lazy(() => import("@/pages/v12/workspace/UserModulesPage"));
const V9F4UserStatsPage = React.lazy(() => import("@/pages/v12/workspace/UserStatsPage"));
const V9F4UserAIAgentsPage = React.lazy(() => import("@/pages/v12/ai/UserAIAgentsPage"));
const V9F4AdminPrdPlaceholderPage = React.lazy(() => import("@/pages/v12/admin/placeholders/AdminPrdCoveragePlaceholderPage"));
// Phase IX.F.5 · PRD V10 Closure · 3 real admin pages + 1 super_admin engine template.
const V9F5AdminModulesPage = React.lazy(() => import("@/pages/v12/admin/AdminModulesPage"));
const V9F5AdminRolesPage = React.lazy(() => import("@/pages/v12/admin/AdminRolesPage"));
const V9F5AdminPaymentsPage = React.lazy(() => import("@/pages/v12/admin/AdminPaymentsPage"));
const V9F5SAEngineDashboard = React.lazy(() => import("@/pages/super_admin/v12/SuperAdminEngineDashboardPage"));
// Phase IX.F.8 · Admin residual (8 real pages) + user AutoSEO long-form.
const V9F8AdminResidualPage = React.lazy(() => import("@/pages/v12/admin/AdminResidualPage"));
const V9F8UserAutoSEOLongForm = React.lazy(() => import("@/pages/v12/ai/UserAutoSEOLongFormPage"));
const MP5AdminFeeConfigPage = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminFeeConfigPage"));
const MP5AdminReviewsPage   = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminReviewsModerationPage"));
const MP5AdminPayoutsPage   = React.lazy(() => import("@/pages/v12/admin/marketplace/AdminPayoutsPage"));
// Phase 17 · Public FAQ page (JSON-LD FAQPage + SSR)
import PublicFAQ from "@/pages/PublicFAQ";
// Phase 10 V30
import AdminMiniLandings from "@/pages/AdminMiniLandings";
import AdminSchedule from "@/pages/AdminSchedule";
import MiniLandingPage from "@/pages/MiniLandingPage";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import AdminSystemSettingsPage from "@/pages/admin/AdminSystemSettingsPage";
import AdminPlaceholderPage from "@/pages/admin/AdminPlaceholderPage";
import AdminWebhooksPage from "@/pages/admin/AdminWebhooksPage";
import AdminLockFunnelPage from "@/pages/admin/AdminLockFunnelPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import UsernameRedirect from "@/pages/UsernameRedirect";
import Legal from "@/pages/Legal";
import SettingsData from "@/pages/SettingsData";
import Notifications from "@/pages/Notifications";
import CookieBanner from "@/components/CookieBanner";
import OnboardingWizard from "@/components/OnboardingWizard";
import { ItalianNudge } from "@/components/LanguageSwitcher";
import LocaleSync from "@/components/LocaleSync";
import RTLController from "@/components/RTLController";
import ModuleGate from "@/components/ModuleGate";
import NotFound from "@/pages/NotFound";
// F12 · Header + Footer refactor minimal premium-dark
import MainLayout from "@/layouts/MainLayout";
import ComingSoonPage from "@/pages/placeholder/ComingSoonPage";
// F13 Wave A · Legal + Contact Nocturna Network Ltd pages
import ContactPage from "@/pages/contact/ContactPage";
import CompliancePage from "@/pages/legal/CompliancePage";
import SlaPage from "@/pages/legal/SlaPage";
import OnboardingPage from "@/pages/legal/OnboardingPage";
// F13 Wave B · Product + Resources marketing pages (lazy-loaded chunks)
const FeaturesPage = React.lazy(() => import("@/pages/product/FeaturesPage"));
const EcosystemsPage = React.lazy(() => import("@/pages/product/EcosystemsPage"));
const AiUniversePage = React.lazy(() => import("@/pages/product/AiUniversePage"));
const AiCivilizationPage = React.lazy(() => import("@/pages/product/AiCivilizationPage"));
const BadgeSystemPage = React.lazy(() => import("@/pages/product/BadgeSystemPage"));
const DocsHubPage = React.lazy(() => import("@/pages/docs/DocsHubPage"));
const UserAutoDocsPage = React.lazy(() => import("@/pages/docs/UserAutoDocsPage"));
// Phase VII.C.2 · Creator payouts
const CreatorEarningsPage = React.lazy(() => import("@/pages/creator/CreatorEarningsPage"));
const CreatorConnectReturnPage = React.lazy(() => import("@/pages/creator/CreatorConnectReturnPage"));
const ApiReferencePage = React.lazy(() => import("@/pages/docs/ApiReferencePage"));
const DocsWebhooksPage = React.lazy(() => import("@/pages/docs/WebhooksPage"));
const CreditEnginePage = React.lazy(() => import("@/pages/docs/CreditEnginePage"));
const LimitsPage = React.lazy(() => import("@/pages/docs/LimitsPage"));
const SeoHubPage = React.lazy(() => import("@/pages/resources/SeoHubPage"));
const AcademyPage = React.lazy(() => import("@/pages/resources/AcademyPage"));
// F18 Phase B · Rendering Engine · public dynamic content pages (lazy-loaded)
const WorldPage = React.lazy(() => import("@/pages/rendering/WorldPage"));
const TopicPage = React.lazy(() => import("@/pages/rendering/TopicPage"));
const ClusterHubPage = React.lazy(() => import("@/pages/rendering/ClusterHubPage"));
const ArticlePage = React.lazy(() => import("@/pages/rendering/ArticlePage"));
const LongTailPage = React.lazy(() => import("@/pages/rendering/LongTailPage"));
// F18 Phase C · Rendering Engine · admin UI (lazy-loaded)
const AdminRenderingPage = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingPage"));
const AdminRenderingPageEditor = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingPageEditor"));
const AdminRenderingAnalytics = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingAnalytics"));
const AdminRenderingLinking = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingLinking"));
// F18 Phase D · new admin pages (lazy)
const LinkingGraphPage = React.lazy(() => import("@/pages/admin/rendering/LinkingGraphPage"));
const AdminRenderingReviewQueue = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingReviewQueue"));
const AdminRenderingSchedules = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingSchedules"));
// F18 Phase E · translation matrix (lazy)
const AdminRenderingTranslations = React.lazy(() => import("@/pages/admin/rendering/AdminRenderingTranslations"));
const AdminRenderingWorldDetail = React.lazy(() =>
    import("@/pages/admin/rendering/AdminRenderingEntityDetails").then((m) => ({ default: m.AdminRenderingWorldDetail })),
);
const AdminRenderingTopicDetail = React.lazy(() =>
    import("@/pages/admin/rendering/AdminRenderingEntityDetails").then((m) => ({ default: m.AdminRenderingTopicDetail })),
);
const AdminRenderingClusterDetail = React.lazy(() =>
    import("@/pages/admin/rendering/AdminRenderingEntityDetails").then((m) => ({ default: m.AdminRenderingClusterDetail })),
);
const AdminRenderingLongTailDetail = React.lazy(() =>
    import("@/pages/admin/rendering/AdminRenderingEntityDetails").then((m) => ({ default: m.AdminRenderingLongTailDetail })),
);
// F20 Phase A · SEO Audit admin UI
const AdminSeoAuditPage = React.lazy(() => import("@/pages/admin/seo/AdminSeoAuditPage"));
// F20 Phase B · SEO Linking Graph admin UI
const AdminSeoGraphPage = React.lazy(() => import("@/pages/admin/seo/AdminSeoGraphPage"));
// F20 Phase C · SEO Optimization admin UI (AI-first rewriter + SERP snippets + JSON-LD)
const AdminSeoOptimizationPage = React.lazy(() => import("@/pages/admin/seo/AdminSeoOptimizationPage"));
// F20 Phase D · SEO Monitoring + Indexing admin UI
const AdminSeoMonitoringPage = React.lazy(() => import("@/pages/admin/seo/AdminSeoMonitoringPage"));
const AdminSeoIndexingPage = React.lazy(() => import("@/pages/admin/seo/AdminSeoIndexingPage"));
// F21 Phase A · Content Engine admin UI
const AdminContentEntitiesPage = React.lazy(() => import("@/pages/admin/content/AdminContentEntitiesPage"));
const AdminContentEntityDetailPage = React.lazy(() => import("@/pages/admin/content/AdminContentEntityDetailPage"));
// F21 Phase B · Content Generator admin UI
const AdminContentGeneratorPage = React.lazy(() => import("@/pages/admin/content/AdminContentGeneratorPage"));
const AdminContentSchedulePage = React.lazy(() => import("@/pages/admin/content/AdminContentSchedulePage"));
const AdminContentQualityPage = React.lazy(() => import("@/pages/admin/content/AdminContentQualityPage"));
const AdminContentSimilarityPage = React.lazy(() => import("@/pages/admin/content/AdminContentSimilarityPage"));
const AdminContentPriorityQueuePage = React.lazy(() => import("@/pages/admin/content/AdminContentPriorityQueuePage"));
// F21 Phase E · Content monitoring dashboard
const AdminContentMonitoringPage = React.lazy(() => import("@/pages/admin/content/AdminContentMonitoringPage"));
// F22 Phase A · Universe Engine · Semantic graph
const AdminSemanticGraphPage = React.lazy(() => import("@/pages/admin/semantic/AdminSemanticGraphPage"));
// F22 Phase E · Universe Dashboard + Alerts
const AdminUniverseDashboardPage = React.lazy(() => import("@/pages/admin/universe/AdminUniverseDashboardPage"));
// F22 Phase F.1 · Marketplace Engine (asset model + generation core)
const AdminMarketplaceF22Page = React.lazy(() => import("@/pages/admin/marketplace/f22/AdminMarketplaceF22Page"));
const AdminStoriesPage = React.lazy(() => import("@/pages/admin/stories/AdminStoriesPage"));
const StoryQualityDashboard = React.lazy(() => import("@/pages/admin/stories/StoryQualityDashboard"));
// F22 Motore 7/14 · Admin · Agent Engine
const AdminAgentsPage = React.lazy(() => import("@/pages/admin/agents/AgentsPage"));
const AdminAgentTasksPanel = React.lazy(() => import("@/pages/admin/agents/AgentTasksPanel"));
const AdminInsightsPage = React.lazy(() => import("@/pages/admin/insights/InsightsPage"));
// F22 Motore 8/14 · Social Automation Engine · Phase A
const AdminSocialPage = React.lazy(() => import("@/pages/admin/social/SocialPage"));
// F22 Motore 9/14 · Affiliate Engine · Phase A
const AdminAffiliatesPage = React.lazy(() => import("@/pages/admin/affiliates/AffiliatesPage"));
// Motore 10/14 · Lead Engine + Outreach Engine (Phase A).
const AdminLeadsListPage = React.lazy(() => import("@/pages/admin/leads/LeadsListPage"));
const AdminSequencesListPage = React.lazy(() => import("@/pages/admin/outreach/SequencesListPage"));
const AdminOutreachPage = React.lazy(() => import("@/pages/admin/outreach/OutreachPage"));
// Phase B · 4 nuove pagine admin (deliveries, pipelines, deliverability, events)
const AdminPhaseBDeliveriesPage = React.lazy(() => import("@/pages/admin/outreach/phase_b/DeliveriesListPage"));
const AdminPhaseBPipelinesPage = React.lazy(() => import("@/pages/admin/outreach/phase_b/PipelinesListPage"));
const AdminPhaseBDeliverabilityPage = React.lazy(() => import("@/pages/admin/outreach/phase_b/DomainHealthPage"));
const AdminPhaseBEventsPage = React.lazy(() => import("@/pages/admin/outreach/phase_b/EventsFeedPage"));
// Motore 10/14 · Phase C · operations pages
const AdminPhaseCScalingPage = React.lazy(() => import("@/pages/admin/outreach/phase_c/scaling/ScalingPage"));
const AdminPhaseCMonitoringPage = React.lazy(() => import("@/pages/admin/outreach/phase_c/monitoring/MonitoringPage"));
const AdminPhaseCAnomaliesPage = React.lazy(() => import("@/pages/admin/outreach/phase_c/anomalies/AnomaliesPage"));
const AdminPhaseCBrandsPage = React.lazy(() => import("@/pages/admin/outreach/phase_c/brands/BrandsPage"));
const AdminPhaseDTenantsPage = React.lazy(() => import("@/pages/admin/outreach/phase_d/tenants/TenantsPage"));
const AdminPhaseDSchedulerPage = React.lazy(() => import("@/pages/admin/outreach/phase_d/scheduler/SchedulerPage"));
const AdminPhaseDAnomalyMlPage = React.lazy(() => import("@/pages/admin/outreach/phase_d/anomaly_ml/AnomalyMlPage"));
const AdminPhaseDAlertsPage = React.lazy(() => import("@/pages/admin/outreach/phase_d/alerts/AlertsPage"));
const AdminPhaseDAbTestingPage = React.lazy(() => import("@/pages/admin/outreach/phase_d/ab_testing/AbTestingPage"));
const AdminPhaseEAiSupervisorPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/ai_supervisor/SupervisorPage"));
const AdminPhaseEDecisionsPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/ai_decisions/DecisionsPage"));
const AdminPhaseEWorkflowsPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/workflows/WorkflowsPage"));
const AdminPhaseEPoliciesPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/policies/PoliciesPage"));
const AdminPhaseEApprovalsPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/approvals/ApprovalsPage"));
const AdminPhaseEAuditPage = React.lazy(() => import("@/pages/admin/outreach/phase_e/audit/AuditPage"));
// Motore 11/14 · CRM Engine · Phase A (contacts/accounts/deals)
const AdminCrmContactsPage = React.lazy(() => import("@/pages/admin/crm/ContactsPage"));
const AdminCrmAccountsPage = React.lazy(() => import("@/pages/admin/crm/AccountsPage"));
const AdminCrmDealsPage = React.lazy(() => import("@/pages/admin/crm/DealsPage"));
const AdminCrmPipelinePage = React.lazy(() => import("@/pages/admin/crm/PipelinePage"));
const AdminCrmRoutingPage = React.lazy(() => import("@/pages/admin/crm/RoutingPage"));
const AdminCrmAgentsPage = React.lazy(() => import("@/pages/admin/crm/AgentsPage"));
const AdminCrmFunnelPage = React.lazy(() => import("@/pages/admin/crm/FunnelPage"));
const AdminCrmAiDecisionsPage = React.lazy(() => import("@/pages/admin/crm/AiDecisionsPage"));
const AdminCrmWorkflowsPage = React.lazy(() => import("@/pages/admin/crm/WorkflowsPage"));
const AdminCrmPoliciesPage = React.lazy(() => import("@/pages/admin/crm/PoliciesPage"));
const AdminCrmApprovalsPage = React.lazy(() => import("@/pages/admin/crm/ApprovalsPage"));
const AdminCrmAuditPage = React.lazy(() => import("@/pages/admin/crm/AuditPage"));
const AdminCrmSupervisorPage = React.lazy(() => import("@/pages/admin/crm/AiSupervisorPage"));

// Motore 12/14 · Press Engine · Phase A (list + editor)
const AdminPressReleasesListPage = React.lazy(() => import("@/pages/admin/press/PressReleasesListPage"));
const AdminPressReleaseEditorPage = React.lazy(() => import("@/pages/admin/press/PressReleaseEditorPage"));
const AdminPressGeneratorPage = React.lazy(() => import("@/pages/admin/press/PressGeneratorPage"));
const AdminPressPreviewPage = React.lazy(() => import("@/pages/admin/press/PressPreviewPage"));
const AdminPressDistributionPage = React.lazy(() => import("@/pages/admin/press/DistributionPage"));
const AdminPressReleaseDistributionPage = React.lazy(() => import("@/pages/admin/press/PressDistributionPage"));
const AdminPressAuthorityDashboard = React.lazy(() => import("@/pages/admin/press/AuthorityDashboard"));
const AdminPressReleaseAuthorityPage = React.lazy(() => import("@/pages/admin/press/PressAuthorityPage"));
// Phase E · Supervisor + Governance
const AdminPressSupervisorPage = React.lazy(() => import("@/pages/admin/press/SupervisorPage"));
const AdminPressDecisionsPage = React.lazy(() => import("@/pages/admin/press/DecisionsPage"));
const AdminPressPoliciesPage = React.lazy(() => import("@/pages/admin/press/PoliciesPage"));
const AdminPressApprovalsPage = React.lazy(() => import("@/pages/admin/press/ApprovalsPage"));
const AdminPressWorkflowsPage = React.lazy(() => import("@/pages/admin/press/WorkflowsPage"));
const AdminPressSupervisorAuditPage = React.lazy(() => import("@/pages/admin/press/SupervisorAuditPage"));
// Motore 13/14 · Phase A · Distribution Engine (additive · zero-touch M12)
const AdminDistributionJobsPage = React.lazy(() => import("@/pages/admin/distribution/JobsPage"));
const AdminDistributionChannelsPage = React.lazy(() => import("@/pages/admin/distribution/ChannelsPage"));
// Motore 13/14 · Phase C · Distribution Engine (additive · zero-touch Phase A/B)
const AdminDistributionEmailPage = React.lazy(() => import("@/pages/admin/distribution/EmailPage"));
const AdminDistributionWebhookPage = React.lazy(() => import("@/pages/admin/distribution/WebhookPage"));
const AdminDistributionRSSPage = React.lazy(() => import("@/pages/admin/distribution/RSSPage"));
const AdminDistributionAtomPage = React.lazy(() => import("@/pages/admin/distribution/AtomPage"));
// Motore 13/14 · Phase D · Wave D.4 · Director UI (additive · zero-touch M12/M13-A/B/C/D.1-3)
const AdminDistributionAuthorityPage = React.lazy(() => import("@/pages/admin/distribution/AuthorityPage"));
const AdminDistributionPoliciesPage = React.lazy(() => import("@/pages/admin/distribution/PoliciesPage"));
const AdminDistributionSupervisorPage = React.lazy(() => import("@/pages/admin/distribution/SupervisorPage"));
const AdminDistributionDecisionsPage = React.lazy(() => import("@/pages/admin/distribution/DecisionsPage"));
const AdminDistributionApprovalsPage = React.lazy(() => import("@/pages/admin/distribution/ApprovalsPage"));
const AdminDistributionWorkflowsPage = React.lazy(() => import("@/pages/admin/distribution/WorkflowsPage"));
import { Navigate } from "react-router-dom";
// Hotfix P1 orphan cleanup · redirect table additive · 10 route legacy aliased.
import { P1_ORPHAN_REDIRECTS } from "@/data/v12_route_cleanup_p1_redirects";
import {
    Sparkles, Boxes, Globe2, Rocket, BookOpen, Zap, Webhook, Coins,
    ShieldCheck, Trophy, Search, Users, GraduationCap, Scale, Clock,
    PlayCircle,
} from "lucide-react";
// Phase 18 · CommunityEngine V1
import CommunityFeed from "@/pages/community/CommunityFeed";
import PostDetail from "@/pages/community/PostDetail";
import ProfilePage from "@/pages/community/ProfilePage";
// Phase 23a · AI Community Foundation
import AICommunityHub from "@/pages/ai-community/AICommunityHub";
import CreatorProfile from "@/pages/ai-community/CreatorProfile";
import AutonomousFeed from "@/pages/ai-community/AutonomousFeed";
import AutonomousGroups from "@/pages/ai-community/AutonomousGroups";
import AutonomousEvents from "@/pages/ai-community/AutonomousEvents";
import AutonomousRooms from "@/pages/ai-community/AutonomousRooms";
import AdminCommunityModeration from "@/pages/AdminCommunityModeration";
// Phase 19 · CommunityEngine V2 (DM · Groups · Channels · Polling)
import MessagesInbox from "@/pages/community/MessagesInbox";
import GroupsDirectory from "@/pages/community/GroupsDirectory";
import GroupDetail from "@/pages/community/GroupDetail";
import CommunitySettings from "@/pages/community/CommunitySettings";
import AdminCommunityDmReports from "@/pages/AdminCommunityDmReports";
// Phase 20 · CommunityEngine V3 (Events + Webinar + Q&A + Calendar)
import EventsDirectory from "@/pages/community/events/EventsDirectory";
import EventsCalendar from "@/pages/community/events/EventsCalendar";
import EventDetail from "@/pages/community/events/EventDetail";
import CreateEventPage from "@/pages/community/events/CreateEventPage";
import AdminCommunityEvents from "@/pages/AdminCommunityEvents";
// Phase 26 · Video Engine V1
import VideoDetail from "@/pages/videos/VideoDetail";
import VideosBrowse from "@/pages/videos/VideosBrowse";
import AdminVideos from "@/pages/admin/videos/AdminVideos";
// Phase 27c · Video Engine V2 · Embed + Pack Detail
import VideoEmbed from "@/pages/videos/VideoEmbed";
import VideoPackDetail from "@/pages/videos/VideoPackDetail";
// Phase 21 · CommunityEngine V4 (Audio + Video Rooms)
import RoomsDirectory from "@/pages/community/rooms/RoomsDirectory";
import CreateRoomPage from "@/pages/community/rooms/CreateRoomPage";
import AudioRoomView from "@/pages/community/rooms/AudioRoomView";
import VideoRoomView from "@/pages/community/rooms/VideoRoomView";
import AdminCommunityRooms from "@/pages/AdminCommunityRooms";
// M14 · Blog Engine · Phase H Wave 3B.3 · i18n EN scope ristretto (additive)
import { I18nProvider } from "@/i18n/I18nContext";
// M14 · Blog Engine · Phase H+1 Wave 4 · additive legacy admin_menu i18n patch
import { applyLegacyAdminMenuPatches } from "@/i18n/legacy_admin_menu_patches";

// Phase H+1 Wave 4 · applica patch i18n legacy al bootstrap (idempotente).
applyLegacyAdminMenuPatches();

// Phase II · Wave D.1 · pre-mount locale basename resolver. Reads
// window.location.pathname BEFORE React mounts so <BrowserRouter> can
// operate against the correct basename (e.g. "/it", "/en-US") without
// having to enumerate all 40 locales as route patterns. Returns ""
// for skip paths (/api, /static, …), in which case the router works
// exactly as before (zero-touch for OAuth callbacks + static assets).
import { getLocaleBasename as _glmsGetLocaleBasename } from "@/i18n/wave_d1/basename";
import LocaleRouteWrapper from "@/components/i18n/LocaleRouteWrapper";
import GeoRedirectHandler from "@/components/i18n/GeoRedirectHandler";
import { CurrencyProvider } from "@/i18n/CurrencyContext";
// FASE H · Shared SPA chrome (Resend-aligned header + footer).
import SharedChromeLayout from "@/components/shared/SharedChromeLayout";

const _GLMS_BASENAME = _glmsGetLocaleBasename();

// FASE P2 · deprecation shim · /cart/success?session_id=xxx forwards to
// the unified /checkout/return. Keeps old Stripe webhooks or bookmarked
// receipts working without surfacing the legacy FASE M polling page.
function CartSuccessRedirect() {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return <Navigate to={`/checkout/return${search}`} replace />;
}

function App() {
    return (
        <BrowserRouter basename={_GLMS_BASENAME}>
            <LocaleRouteWrapper>
            <AuthProvider>
                <AuthModalProvider>
                <CartProvider>
                <PhaseHSuperAdminProvider>
                <I18nProvider>
                <CurrencyProvider>
                <LocaleSync />
                <RTLController />
                <AuthCallbackHandler />
                {/* Phase II · Wave D.6b · First-visit geo-suggest banner. */}
                <GeoRedirectHandler />
                {/* Phase I Wave 2.4 · Impersonate mode persistent banner (top of app). */}
                <ImpersonationBanner />
                {/* Phase IV · Wave E · Federated Search · Portal-based ⌘K bar.
                    Mounted here so useNavigate() has access to the router
                    context. Zero touch on header components · no DOM injection. */}
                <Suspense fallback={null}>
                    <FederatedSearchBar />
                </Suspense>
                <AnalyticsTracker>
                <Routes>
                    {/* FASE J · All legacy SPA landing routes (`/`, `/v2`,
                        `/v3`, `/landing-v1`, admin previews, locale
                        prefixes) redirect via <ForceReload /> to the
                        authoritative Next.js SSR landing served at `/`.
                        The SPA router never renders a landing component
                        anymore — Next.js owns the public landing. */}
                    {/* Wave Marketing Parity V4 · 2026-11-01
                        Homepage · `/` now serves V12 Achromatic Strict
                        homepage natively. ForceReload safety nets remain
                        in place for the legacy preview landings (`/v2`,
                        `/v3`, `/landing-v1`) and locale-root cases
                        (`/it`, `/en-US`, …) which still rely on auth-
                        aware fallback. */}
                    <Route path="/" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12HomePage /></Suspense>} />
                    <Route path="/v2" element={<ForceReload to="/" />} />
                    <Route path="/v3" element={<ForceReload to="/" />} />
                    <Route path="/landing-v1" element={<ForceReload to="/" />} />
                    <Route path="/admin/preview/landing-v1" element={<ForceReload to="/" />} />
                    <Route path="/admin/preview/landing-v2" element={<ForceReload to="/" />} />
                    <Route path="/admin/preview/landing-v3" element={<ForceReload to="/" />} />
                    {/* Legacy locale-root URLs (`/en-US`, `/en-US/*`).
                        Hotfix 2026-10 · ora intercettati server-side da
                        setupProxy.js (308 strip) + Next.js redirects. Le
                        route SPA sotto restano solo come guardia fail-safe
                        nel caso una build legacy del proxy sia in cache.
                        `en-US` non è nel set di 14 locali canoniche. */}
                    <Route path="/en-US" element={<ForceReload to="/" />} />
                    <Route path="/en-US/*" element={<ForceReload to="/" />} />
                    <Route path="/community/discover" element={<CommunityDiscover />} />
                    {/* Locale-prefixed public routes: hydration matches the
                        SEO SSR head served by the dev-server middleware.
                        We keep only PUBLIC pages here — private routes
                        (dashboard, admin, ...) never carry a locale prefix.
                        React Router 7 has no regex path support, so we
                        enumerate the 15 locales explicitly.
                        FASE J · the locale-root entry no longer renders
                        <Landing/> — it force-reloads to the SSR landing. */}
                    {["it", "es", "fr", "de", "pt", "nl", "pl", "tr", "ja", "zh-CN", "ru", "ar", "ko", "hi"].map((lc) => (
                        <React.Fragment key={lc}>
                            <Route path={`/${lc}`} element={<ForceReload to="/" />} />
                            {/* 2026-11-01 · explicit trailing-slash variant ·
                                React Router v7 does not auto-match /it/ to /it. */}
                            <Route path={`/${lc}/`} element={<ForceReload to="/" />} />
                            {/* F83 · locale-prefixed /pricing used to render the
                                unified USD V12 page. F84 redirects to
                                /v2/subscription (single source of truth · the
                                UnifiedSubscriptionPage picks public vs auth UX
                                internally). Keep the locale route so inbound
                                SEO links & translated nav still resolve. */}
                            <Route path={`/${lc}/pricing`} element={<Navigate to="/app/subscription" replace />} />
                            <Route path={`/${lc}/blog`} element={<Blog />} />
                            <Route path={`/${lc}/blog/:slug`} element={<BlogPost />} />
                        </React.Fragment>
                    ))}
                    {/* Emergent Auth callback lands on /profile with #session_id=... */}
                    <Route path="/profile" element={<Profile />} />

                    {/* F8.7b · Direct Auth · password reset landing page */}
                    <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

                    {/* F9.7 WAVE 2I · deep-link login/signup/forgot pages.
                        Auto-opens the AuthModal (Google + email/password form both visible).
                        Already-authenticated users are redirected to ?redirect or /dashboard. */}
                    <Route path="/login" element={<LoginRoute tab="login" />} />
                    <Route path="/signup" element={<LoginRoute tab="signup" />} />
                    <Route path="/forgot-password" element={<LoginRoute tab="forgot" />} />

                    {/* Phase 18 · CommunityEngine V1 · public routes */}
                    <Route path="/community" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12CommunityPage /></Suspense>} />
                    <Route path="/community/post/:id" element={<PostDetail />} />
                    <Route
                        path="/community/settings"
                        element={
                            <ProtectedRoute>
                                <CommunitySettings />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/community/:category" element={<CommunityFeed />} />
                    <Route path="/profile/:username" element={<ProfilePage />} />
                    <Route
                        path="/admin/community/moderation"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminCommunityModeration />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 19 · CommunityEngine V2 · DM · Groups · Channels · Polling */}
                    <Route
                        path="/messages"
                        element={
                            <ProtectedRoute>
                                <MessagesInbox />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/messages/:username"
                        element={
                            <ProtectedRoute>
                                <MessagesInbox />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/groups" element={<GroupsDirectory />} />
                    <Route path="/groups/:groupId" element={<GroupDetail />} />
                    <Route path="/groups/:groupId/channel/:channelId" element={<GroupDetail />} />
                    <Route
                        path="/admin/community/dm-reports"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminCommunityDmReports />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 20 · CommunityEngine V3 · Events / Webinar / Workshop / Q&A / Calendar */}
                    <Route path="/events" element={<EventsDirectory />} />
                    <Route path="/events/calendar" element={<EventsCalendar />} />
                    <Route
                        path="/events/create"
                        element={
                            <ProtectedRoute>
                                <CreateEventPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/events/:eventId" element={<EventDetail />} />
                    <Route path="/events/live/:eventId" element={<EventDetail />} />
                    <Route
                        path="/admin/community/events"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminCommunityEvents />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 21 · CommunityEngine V4 · Audio + Video Rooms (UI placeholder) */}
                    <Route path="/rooms" element={<RoomsDirectory />} />
                    <Route
                        path="/rooms/create"
                        element={
                            <ProtectedRoute>
                                <CreateRoomPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rooms/audio/:roomId"
                        element={
                            <ProtectedRoute>
                                <AudioRoomView />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rooms/video/:roomId"
                        element={
                            <ProtectedRoute>
                                <VideoRoomView />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 23a · AI Community Foundation (public + kill-switch aware) */}
                    <Route path="/ai" element={<AICommunityHub />} />
                    <Route path="/ai/creator/:creator_id" element={<CreatorProfile />} />
                    <Route path="/ai/autonomous-feed" element={<AutonomousFeed />} />
                    {/* Phase 23b · AI Groups/Events/Rooms (public + sub-flag kill-switch aware) */}
                    <Route path="/ai/autonomous-groups" element={<AutonomousGroups />} />
                    <Route path="/ai/autonomous-events" element={<AutonomousEvents />} />
                    <Route path="/ai/autonomous-rooms" element={<AutonomousRooms />} />
                    <Route
                        path="/admin/community/rooms"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminCommunityRooms />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={<Navigate to="/app/dashboard" replace />}
                    />
                    {/* Hotfix bare-parent redirects · fix 404 su /app, /app/ai, /app/social, /app/content.
                        Zero-touch sulle route child esistenti · Navigate puro (SPA, nessun reload). */}
                    <Route
                        path="/app"
                        element={<Navigate to="/app/dashboard" replace />}
                    />
                    <Route
                        path="/app/ai"
                        element={<Navigate to="/app/ai/agents" replace />}
                    />
                    <Route
                        path="/app/social"
                        element={<Navigate to="/app/social/discovery" replace />}
                    />
                    <Route
                        path="/app/content"
                        element={<Navigate to="/app/content/articles" replace />}
                    />
                    {/* Hotfix EN↔IT alias · evita 404 su path inglesi comuni ·
                        tutti puntano al path italiano canonico esistente in /app/account/*. */}
                    <Route
                        path="/app/profile"
                        element={<Navigate to="/app/account/profilo" replace />}
                    />
                    <Route
                        path="/app/account"
                        element={<Navigate to="/app/account/profilo" replace />}
                    />
                    <Route
                        path="/app/settings"
                        element={<Navigate to="/app/account/profilo" replace />}
                    />
                    {/* P1 orphan cleanup · Family B credits aliases (6) + Family D admin leaks (4).
                        Table-driven additive · tutte le from-path NON esistevano in App.js prima.
                        See /app/memory/orphan_p1_cleanup_plan.md per full breakdown. */}
                    {P1_ORPHAN_REDIRECTS.map(({ from, to }) => (
                        <Route
                            key={`p1-redirect-${from}`}
                            path={from}
                            element={<Navigate to={to} replace />}
                        />
                    ))}
                    <Route
                        path="/dashboard/legacy"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 2.5 · DashboardRouter still reachable via /dashboard-router
                        for admins who need A/B legacy flows. The default `/dashboard`
                        now hard-redirects to V12. */}
                    <Route
                        path="/dashboard-router"
                        element={
                            <ProtectedRoute>
                                <DashboardRouter />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/preview/dashboard-v3"
                        element={
                            <ProtectedRoute adminOnly>
                                <DashboardV3 />
                            </ProtectedRoute>
                        }
                    />
                    {/* P1 orphan cleanup · Family B · /credits legacy top-level
                        → canonizzato su /app/credits. Pagina `Credits` resta
                        importata ma unreachable da questo path · nessuna cancellazione. */}
                    <Route
                        path="/credits"
                        element={<Navigate to="/app/credits" replace />}
                    />
                    <Route
                        path="/leads"
                        element={
                            <ProtectedRoute>
                                <Leads />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/leads/saved"
                        element={
                            <ProtectedRoute>
                                <SavedLeads />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/checkout/success"
                        element={<CheckoutSuccess />}
                    />
                    <Route
                        path="/checkout/failed"
                        element={
                            <ProtectedRoute>
                                <CheckoutFailed />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 2 · Outreach (gated on plan.modules.includes("outreach")) */}
                    <Route
                        path="/outreach"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <Outreach />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outreach/templates"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <OutreachTemplates />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outreach/sequences"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <OutreachSequences />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outreach/campaigns"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <OutreachCampaigns />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outreach/campaigns/new"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <OutreachCampaignNew />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/outreach/campaigns/:id"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="outreach">
                                    <OutreachCampaignDetail />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

                    {/* Phase 3 · Plans / Billing / Affiliate */}
                    {/* Phase I Wave 1.8 · /pricing legacy redirect fix.
                        PricingF11 (F11 Phase A sealed) crashes with
                        `t('plans.list') || []).map is not a function`
                        when the i18n bundle doesn't ship the legacy key.
                        F81 fix · the previous `Navigate to="/app/subscription"`
                        broke the Pricing CTAs for ALL unauthenticated users
                        (header + landing teaser + sidebar) because
                        `/v2/subscription` is behind an auth guard.
                        F82 fix · unified the pricing surface on a single
                        authoritative component `PricingV12USD` (USD V12
                        catalogue · Starter $199 / Growth $399 POPULAR /
                        Scale $899 / Enterprise contact · 5 credit packs).
                        PricingV24a is deprecated and only reachable via
                        the explicit `/pricing/v24a` legacy alias for
                        backwards compatibility.
                        F84 fix · `/v2/subscription` is now auth-adaptive
                        (public shell OR authenticated V12 page). `/pricing`
                        and `/{lc}/pricing` redirect here · one URL for
                        everything pricing-related. Explicit deprecated
                        aliases `/pricing/v24a` and `/pricing/legacy`
                        remain for operator audit only. */}
                    {/* Wave VII.A R4 · `/pricing` canonical è ora la nuova V12
                        pricing page (Subscription + Credit Packs + Cost Table).
                        Deprecated redirect legacy a /app/subscription è stato
                        rimosso in favore della landing SPA V12. Alias protetto
                        `/app/pricing` resta disponibile per link sidebar wrapped
                        in V12UserLayout. `/credits/pricing` ora redirect qui
                        per backward compat (R3 era un workaround). */}
                    <Route path="/pricing" element={<V7APricingPage />} />
                    {/* Wave Marketing Parity V4 · 8 PORT routes (V12 Achromatic Strict) */}
                    <Route path="/suites" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12SuitesPage /></Suspense>} />
                    <Route path="/suites/compare" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12SuitesComparePage /></Suspense>} />
                    <Route path="/suites/:slug" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12SuiteDetailPage /></Suspense>} />
                    <Route path="/products/lead-engine" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12ProductLeadEnginePage /></Suspense>} />
                    <Route path="/products/marketplace" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12ProductMarketplacePage /></Suspense>} />
                    <Route path="/leaderboard" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12LeaderboardPage /></Suspense>} />
                    <Route path="/stories/:slug" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12StoryDetailPage /></Suspense>} />
                    <Route path="/status" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12StatusPage /></Suspense>} />
                    <Route path="/credits/pricing" element={<Navigate to="/pricing" replace />} />
                    <Route path="/pricing/v24a" element={<PricingV24a />} />
                    <Route path="/pricing/legacy" element={<Pricing />} />
                    {/* Wave VII.B · Admin Email Logs · protected admin only */}
                    <Route path="/admin/email" element={
                        <ProtectedRoute adminOnly>
                            <V7BAdminEmailLogsPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.A · Admin AI Engine orchestrator · protected admin only */}
                    <Route path="/admin/ai-engine" element={
                        <ProtectedRoute adminOnly>
                            <V8AAdminAIEnginePage />
                        </ProtectedRoute>
                    } />
                    {/* Wave X.E · Business KPI dashboard · admin only */}
                    <Route path="/app/admin/kpi" element={
                        <ProtectedRoute adminOnly>
                            <WaveXEBusinessKPIDashboard />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.B · Admin Auto-Creation (AutoCreationEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-creation" element={
                        <ProtectedRoute adminOnly>
                            <V8BAdminAutoCreationPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.C · Admin Auto-Expansion (AutoExpansionEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-expansion" element={
                        <ProtectedRoute adminOnly>
                            <V8CAdminAutoExpansionPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.D · Admin Auto-Marketplace (AutoMarketplaceEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-marketplace" element={
                        <ProtectedRoute adminOnly>
                            <V8DAdminAutoMarketplacePage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.E · Admin Auto-SEO (AutoSEOEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-seo" element={
                        <ProtectedRoute adminOnly>
                            <V8EAdminAutoSEOPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.F · Admin Auto-Governance (AutoGovernanceEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-governance" element={
                        <ProtectedRoute adminOnly>
                            <V8FAdminAutoGovernancePage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.G · Admin Auto-Routing (AutoRoutingEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-routing" element={
                        <ProtectedRoute adminOnly>
                            <V8GAdminAutoRoutingPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.H · Admin Auto-Recommendation (AutoRecommendationEngine LIVE) */}
                    <Route path="/admin/ai-engine/auto-recommendation" element={
                        <ProtectedRoute adminOnly>
                            <V8HAdminAutoRecommendationPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.I · Admin Onboarding + Support combo (2 engines LIVE) */}
                    <Route path="/admin/ai-engine/onboarding-support" element={
                        <ProtectedRoute adminOnly>
                            <V8IAdminOnboardingSupportPage />
                        </ProtectedRoute>
                    } />
                    {/* Wave VIII.J · Admin Visionary Engines mega-combo (4 engines LIVE · 13/13) */}
                    <Route path="/admin/ai-engine/visionary" element={
                        <ProtectedRoute adminOnly>
                            <V8JAdminVisionaryEnginesPage />
                        </ProtectedRoute>
                    } />
                    <Route
                        path="/admin/payments"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminPayments />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase 24b · Upsell AI Engine V2 · admin surfaces */}
                    <Route
                        path="/admin/upsell"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminUpsell />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ai/upsell-log"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminUpsell />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ai/upsell-preview"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminUpsell />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase 24c · SubscriptionEngine · user + admin surfaces */}
                    <Route
                        path="/account/subscriptions"
                        element={
                            <ProtectedRoute>
                                <AccountSubscriptions />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/account/billing"
                        element={
                            <ProtectedRoute>
                                <AccountBilling />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/subscriptions"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminSubscriptions />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase 24d · TicketingEngine */}
                    <Route path="/event/:id/ticket" element={<EventTicketPurchase />} />
                    <Route path="/tickets/success/:ticket_id" element={<TicketPurchaseSuccess />} />
                    <Route
                        path="/account/tickets"
                        element={
                            <ProtectedRoute>
                                <AccountTickets />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/tickets"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminTickets />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/host/events/:id/checkin"
                        element={
                            <ProtectedRoute>
                                <EventCheckin />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/billing"
                        element={
                            <ProtectedRoute>
                                <Billing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/upgrade"
                        element={
                            <ProtectedRoute>
                                <Upgrade />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/affiliate"
                        element={
                            <ProtectedRoute>
                                <Affiliate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/enterprise-leads"
                        element={
                            <ProtectedRoute adminOnly>
                                <ModuleGate role="admin">
                                    <AdminEnterpriseLeads />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/affiliate-payouts"
                        element={
                            <ProtectedRoute adminOnly>
                                <ModuleGate role="admin">
                                    <AdminAffiliatePayouts />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase VI.5 Wave B · R1 Issue B · legacy admin path wrapped
                        in V12AdminLayout so legacy /admin/economy renders in the
                        SAME shell as the V12 mount /admin/v2/ai/economy +
                        /admin/v12/economy/overview (policy 3a strict pair). */}
                    <Route
                        path="/admin/economy"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminLayout>
                                        <ModuleGate role="admin">
                                            <AdminEconomy />
                                        </ModuleGate>
                                    </V12AdminLayout>
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase 4 · Universe / Civilization                                  */}
                    {/* Wave A.4 cleanup · ``/universe/legacy`` + ``/universe/worlds`` + the  */}
                    {/* Universe.jsx + UniverseWorlds.jsx pages removed. The Phase II Wave   */}
                    {/* A.3 hub (``UniverseHubDashboardPage``) now owns ``/universe`` and     */}
                    {/* exposes agents / tasks / inbox as nested routes.                     */}
                    <Route
                        path="/civilization"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="civilization">
                                    <Civilization />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/civilization/leaderboard"
                        element={<CivilizationLeaderboard />}
                    />
                    <Route
                        path="/civilization/badges"
                        element={
                            <ProtectedRoute>
                                <ModuleGate module="civilization">
                                    <CivilizationBadges />
                                </ModuleGate>
                            </ProtectedRoute>
                        }
                    />
                    {/* Public profile — no auth required */}
                    <Route
                        path="/civilization/profile/:userId"
                        element={<CivilizationProfile />}
                    />
                    <Route path="/account-suspended" element={<AccountSuspended />} />

                    {/* ---------- Phase 5 · Module Builder + Marketplace ---------- */}
                    {/* Phase 6B: made public read (auth prompted on purchase/run). */}
                    <Route path="/modules" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12ModulesPage /></Suspense>} />
                    <Route path="/modules/:slug" element={<ModuleRun />} />
                    {/* Hotfix 2026-10 · legacy /marketplace → V10 catalog canonical. */}
                    <Route path="/marketplace" element={<Navigate to="/marketplace/v10" replace />} />
                    {/* Phase VII · Wave Marketplace V10 · MP.7 · Sidebar persistente ·
                        tutte le route V10 wrappate in V12UserLayout per esporre
                        la sidebar MP.6 role-aware durante l'intero flow. */}
                    {/* Hotfix 2026-10 · Marketplace V10 catalog PUBLIC · no auth gate on read.
                        Login enforced inside the page on write actions (cart / wishlist / purchase). */}
                    <Route path="/marketplace/v10" element={<V12UserLayoutOrPublic><MP1CatalogPage /></V12UserLayoutOrPublic>} />
                    <Route path="/marketplace/hub" element={<ProtectedRoute><V12UserLayout><MP7HubPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Wave VII.A · Stripe Credit Packs. */}
                    <Route path="/credits/packs" element={<ProtectedRoute><V12UserLayout><V7ACreditPacksPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/checkout" element={<ProtectedRoute><V12UserLayout><V7ACheckoutPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/credits/success" element={<ProtectedRoute><V12UserLayout><V7ASuccessPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Wave VII.A R3 · Pricing page · public SPA under /credits/pricing namespace.
                        Nota: `/pricing` canonical è già occupato dalla Next.js SSR pricing page
                        (V13 subscription tiers) servita in production dall'ingress Kubernetes.
                        Per evitare conflitto, la nostra SPA pricing (credit packs + cost table)
                        vive sotto `/credits/pricing` che è già nel SPA-bypass regex del
                        setupProxy. La route public NON è wrapped in V12UserLayout (che
                        hard-redirect su !user → `/`) · usa invece SidebarLayout che
                        fallbacka a header pubblico se anonymous (vedi SidebarLayout.jsx:67).
                        Alias `/app/pricing` resta protetto + wrapped V12UserLayout per i
                        link sidebar dei loggati. */}
                    <Route path="/credits/pricing" element={<V7APricingPage />} />
                    {/* FIX 2 · /app/pricing unificato con /pricing (editorial V12
                        VERBATIM). PricingPage usa SidebarLayout internamente che
                        rende layout coerente per utenti loggati (user_v12 shell)
                        e fallbacka a header pubblico se anonymous. Deprecato
                        UpgradeView legacy che mostrava copy diverso. */}
                    <Route path="/app/pricing" element={<ProtectedRoute><V7APricingPage /></ProtectedRoute>} />
                    <Route path="/credits/failed" element={<ProtectedRoute><V12UserLayout><V7AFailedPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/checkout/success" element={<ProtectedRoute><V12UserLayout><V7ASuccessPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/checkout/failed" element={<ProtectedRoute><V12UserLayout><V7AFailedPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/wishlist" element={<ProtectedRoute><V12UserLayout><MP1WishlistPage /></V12UserLayout></ProtectedRoute>} />
                    {/* FASE L round-2 · Legacy `/marketplace/cart` redirects to `/cart`.
                        Full-URL unification · the SPA router handles both via a single
                        `<Navigate replace />` (no full page reload — stays in SPA). */}
                    <Route path="/marketplace/cart" element={<Navigate to="/cart" replace />} />
                    {/* Phase VII · Wave Marketplace V10 · MP.2 Creator Studio. */}
                    <Route path="/marketplace/creator/apply" element={<ProtectedRoute><V12UserLayout><MP2CreatorOnboardingPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator" element={<ProtectedRoute><V12UserLayout><MP2CreatorDashboardPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator/assets" element={<ProtectedRoute><V12UserLayout><MP2CreatorAssetListPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator/assets/new" element={<ProtectedRoute><V12UserLayout><MP2CreatorAssetEditorPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator/assets/:id/edit" element={<ProtectedRoute><V12UserLayout><MP2CreatorAssetEditorPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator/earnings" element={<ProtectedRoute><V12UserLayout><MP2CreatorEarningsPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Phase VII · Wave Marketplace V10 · MP.3 Admin Moderation. */}
                    <Route path="/admin/marketplace/v10" element={<ProtectedRoute adminOnly><V12UserLayout><MP3AdminOverviewPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/moderation" element={<ProtectedRoute adminOnly><V12UserLayout><MP3ModerationQueuePage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/assets" element={<ProtectedRoute adminOnly><V12UserLayout><MP3AdminAssetsPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/creators" element={<ProtectedRoute adminOnly><V12UserLayout><MP3AdminCreatorsPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/audit" element={<ProtectedRoute adminOnly><V12UserLayout><MP3AdminAuditPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Wave Marketplace V10 · MP.5 · Final Consolidation */}
                    <Route path="/admin/marketplace/fee-config" element={<ProtectedRoute adminOnly><V12UserLayout><MP5AdminFeeConfigPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/reviews" element={<ProtectedRoute adminOnly><V12UserLayout><MP5AdminReviewsPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/admin/marketplace/payouts" element={<ProtectedRoute adminOnly><V12UserLayout><MP5AdminPayoutsPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Hotfix 2026-10 · Asset detail PUBLIC · same policy as /marketplace/v10. */}
                    <Route path="/marketplace/v10/asset/:id" element={<V12UserLayoutOrPublic><MP5AssetDetailPage /></V12UserLayoutOrPublic>} />
                    {/* Phase VII · Wave Marketplace V10 · MP.4 Asset Types pages. */}
                    <Route path="/marketplace/service-bookings" element={<ProtectedRoute><V12UserLayout><MP4MyBookingsPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/token-redemptions" element={<ProtectedRoute><V12UserLayout><MP4MyRedemptionsPage /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/marketplace/creator/service-bookings" element={<ProtectedRoute><V12UserLayout><MP4CreatorBookingsPage /></V12UserLayout></ProtectedRoute>} />
                    {/* Phase VI.5 Wave A · R2 Issue 2 · /marketplace/library
                        legacy route is now co-located inside the Wave A alias
                        block below for strict ordering (right before the
                        dynamic /marketplace/:slug route · see line ~1282). */}
                    <Route path="/marketplace/:slug" element={<MarketplaceAsset />} />
                    {/* Phase VI.5 Wave B · R1 Issue B · legacy admin path wrapped
                        in V12AdminLayout (pair with /admin/v2/product/modules). */}
                    <Route
                        path="/admin/modules"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminLayout>
                                        <AdminModules />
                                    </V12AdminLayout>
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/marketplace"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminMarketplace />
                            </ProtectedRoute>
                        }
                    />

                    {/* ---------- Phase 6A · Blog legacy routes · superseded by Phase VI.A ---------- */}
                    {/* The /blog + /blog/:slug Wave VI.A public routes (around line 1940) take over.      */}
                    {/* Localized variants /${lc}/blog + /${lc}/blog/:slug remain served by Blog/BlogPost.  */}

                    {/* ---------- Phase 6B · Legal / GDPR / Notifications ---------- */}
                    <Route path="/legal/:slug" element={<Legal />} />
                    <Route
                        path="/settings/data"
                        element={
                            <ProtectedRoute>
                                <SettingsData />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/notifications"
                        element={<Navigate to="/app/notifications" replace />}
                    />

                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    {/* Hotfix FASE 2 · V12 Achromatic Strict · Admin Hub launcher · adminOnly. */}
                    <Route
                        path="/admin/overview"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-black" />}>
                                    <AdminHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Hotfix FASE B · V12 Achromatic Strict · User Hub launcher · user-accessible. */}
                    <Route
                        path="/app/launcher"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-black" />}>
                                    <UserHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminBlog />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase A · Content Generation (additive, zero-touch legacy /admin/blog) */}
                    <Route
                        path="/admin/blog-engine/articles"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14ArticlesListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/articles/new"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14ArticleNewPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/articles/:id"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14ArticleDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Hotfix 2026-10 · user topic suggestions review (M14 adjunct) */}
                    <Route
                        path="/admin/blog-engine/topic-suggestions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <BlogM14TopicSuggestionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase D · Distribution Integration (additive, zero-touch Phase A/B/C) */}
                    <Route
                        path="/admin/blog-engine/distribution/partners"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14DistributionPartnersPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/distribution/jobs"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14DistributionJobsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase E · Analytics Engine (additive) */}
                    <Route
                        path="/admin/blog-engine/analytics"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14AnalyticsDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase F · Recycling Engine (additive) */}
                    <Route
                        path="/admin/blog-engine/recycling/policy"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14RecyclingPolicyPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/recycling/queue"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14RecyclingQueuePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/recycling/suggestions"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14RecyclingSuggestionsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase G Wave 4 · SERP + Social admin UI (additive) */}
                    <Route
                        path="/admin/blog-engine/serp/dashboard"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14SerpDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/serp/sites"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14SerpSitesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/social/credentials"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14SocialCredentialsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/social/dispatch-log"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14SocialDispatchLogPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Blog Engine · Phase H Wave 1 · admin UI (additive, zero-touch Phase G sealed) */}
                    <Route
                        path="/admin/blog-engine/phase-h/serp/dashboard"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14PhaseHSerpDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/phase-h/social/insights"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14PhaseHSocialInsights />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog-engine/phase-h/social/batch-retry"
                        element={
                            <ProtectedRoute adminOnly>
                                <BlogM14PhaseHSocialBatchRetry />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Phase H Wave 2B · V12 Admin Dashboard parallel layout */}
                    <Route
                        path="/admin/v2/dashboard"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase II · Wave A.3 · Universe Hub routes (zero-touch additive) */}
                    <Route
                        path="/universe"
                        element={
                            <ProtectedRoute>
                                <UniverseHubDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/universe/agents"
                        element={
                            <ProtectedRoute>
                                <UniverseAgentRegistryPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/universe/tasks"
                        element={
                            <ProtectedRoute>
                                <UniverseTaskGraphListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/universe/tasks/builder"
                        element={
                            <ProtectedRoute>
                                <UniverseTaskGraphBuilderPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/universe/tasks/builder/:taskId"
                        element={
                            <ProtectedRoute>
                                <UniverseTaskGraphBuilderPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/universe/inbox"
                        element={
                            <ProtectedRoute>
                                <UniverseMessageInboxPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* P1 orphan cleanup · Family A · Marketplace V8 legacy routes
                        DROP + 301 redirect to /marketplace/v10 (V10 canonical · vedi
                        App.js:1548 e MP.7 Hotfix R7 :3062). Fix hop · evita catena
                        /marketplace/v8 → /app/marketplace → /marketplace/v10.
                        Imports MarketplaceStorefrontPageB4 etc. restano · pagine
                        reachable programmaticamente · solo path URL deprecati. */}
                    <Route
                        path="/marketplace/v8"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/listings/:listingId"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/listings/by-slug/:slug"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/sellers/:sellerId"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/my-listings"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/my-listings/new"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/my-listings/:listingId/edit"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/my-purchases"
                        element={<Navigate to="/marketplace/v10" replace />}
                    />
                    <Route
                        path="/marketplace/v8/admin/moderation"
                        element={<Navigate to="/admin/marketplace/v10" replace />}
                    />
                    <Route
                        path="/admin/v2"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v2/social/dispatch-log-live"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SSEDispatchLogPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v2/social/thread"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12ThreadAdminPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v2/social/media"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12MediaAdminPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* M14 · Phase H+1 Mini-Wave 5 · User Dashboard V12 (zero-touch · parallel shell) */}
                    <Route
                        path="/v2/dashboard"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12UserDashboardPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave VIII Frontend Integration · OnboardingWizard
                        (OnboardingEngine LIVE · Wave VIII.I). Page wraps
                        itself in V12UserLayout.
                        2026-11-01 · path renamed /onboarding → /app/onboarding
                        to free the public /onboarding slot for the V12
                        marketing parity page. */}
                    <Route
                        path="/app/onboarding"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V8IOnboardingWizardPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.A · AI Economy · user reputation page (self-wraps V12UserLayout). */}
                    <Route
                        path="/app/reputation"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9AReputationPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.A · AI Economy · admin dashboard. */}
                    <Route
                        path="/admin/ai-economy"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9AAdminAIEconomyPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.B · AI Civilization Operations · admin only. */}
                    <Route
                        path="/admin/ai-civ-ops"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9BAdminCivilizationOpsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.C · Universe + Ecosystem Hub user-facing. */}
                    <Route
                        path="/app/universe"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9CUniverseHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/universe/instance/:instanceId"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9CUniverseInstancePage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/ecosystem"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9CEcosystemHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.D · Automations Hub + Workflow builder. */}
                    <Route
                        path="/app/automations"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9DAutomationsHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/automations/workflows"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9DWorkflowBuilderPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave IX.E · Ranking Hub + public profile. */}
                    <Route
                        path="/app/ranking"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9ERankingHubPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/u/:userId"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <V9EPublicProfilePage />
                            </Suspense>
                        }
                    />
                    {/* Phase IX.F.2 · Suites v10 (list / detail / owned). */}
                    <Route
                        path="/app/suites"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F2SuitesListPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/suites/me"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F2SuitesOwnedPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/suites/:suiteId"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F2SuiteDetailPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.4 · A1 · Moduli attivi (PRD V10 gap closure) */}
                    <Route
                        path="/app/modules"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F4UserModulesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.4 · A2 · Statistiche personali */}
                    <Route
                        path="/app/stats"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F4UserStatsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.4 · A3 · AI Agents listing */}
                    <Route
                        path="/app/ai/agents"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F4UserAIAgentsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.4 · Admin PRD V10 coverage placeholder */}
                    <Route
                        path="/admin/prd/:slug"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F4AdminPrdPlaceholderPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.5 · Admin REAL pages (modules, roles, payments) */}
                    <Route
                        path="/admin/f5/modules"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F5AdminModulesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/f5/roles"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F5AdminRolesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/f5/payments"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F5AdminPaymentsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.5 · Super Admin engines dashboard template */}
                    <Route
                        path="/sa/engines/:slug"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F5SAEngineDashboard />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.8 · Admin residual (8 real pages replacing f4 placeholders) */}
                    <Route
                        path="/admin/f8/:slug"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F8AdminResidualPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase IX.F.8 · User AutoSEO long-form articles */}
                    <Route
                        path="/app/ai/seo/long-form"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V9F8UserAutoSEOLongForm />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 2.3 (F81) · full referral dashboard. */}
                    <Route
                        path="/v2/referral"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V2ReferralPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 2.4 · Admin Dev Toolkit · super_admin ONLY (R2 fix). */}
                    <Route
                        path="/v2/admin/dev-toolkit"
                        element={
                            <ProtectedRoute>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V2AdminDevToolkitPage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* P1 orphan cleanup · Family B · /v2/credits variant
                        → canonizzato su /app/credits. V12CreditPackPurchasePage
                        resta importata ma unreachable da questo path legacy. */}
                    <Route
                        path="/v2/credits"
                        element={<Navigate to="/app/credits" replace />}
                    />
                    {/* Phase I Wave 1.5 · Subscription + Checkout (zero-touch legacy /checkout/*)
                        Phase I Wave 1.21 (F84) · auth-gate removed · UnifiedSubscriptionPage
                        picks public vs authenticated UX internally. */}
                    <Route
                        path="/v2/subscription"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <UnifiedSubscriptionPage />
                            </Suspense>
                        }
                    />
                    {/* Phase I Wave 1.9 · shared ROI calc landing · same page, query-aware */}
                    <Route
                        path="/v2/subscription/calc"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <UnifiedSubscriptionPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/v2/billing"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <UnifiedSubscriptionPage />
                            </Suspense>
                        }
                    />
                    {/* Phase I Wave 1.22 (F85) · ~55 V12 placeholder routes.
                        Every sidebar item now points to `/v2/<slug>` and
                        resolves to <V12ComingSoonPage /> under the V12
                        user shell · SPA Link navigation · zero full
                        reload, zero auth-state flush, zero catch-all.
                        See `data/v12_coming_soon_registry.js`. */}

                    {/* Phase I Wave 2.5 R2 · 3 plan-gated demo routes.
                        Mounted BEFORE the F85 placeholder map so they
                        take precedence over any `/v2/<slug>` fallback.
                        Super-admin / admin bypass applies automatically. */}
                    <Route
                        path="/v2/seo/dashboard"
                        element={
                            <GatedRoute featureKey="seo_master_base">
                                <V2SeoMasterDashboardShell />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/v2/blog/engine"
                        element={
                            <GatedRoute featureKey="blog_engine_10">
                                <V2BlogEngineShell />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/v2/ai/agents"
                        element={
                            <GatedRoute featureKey="ai_agents_preconfigured">
                                <V2AIAgentsShell />
                            </GatedRoute>
                        }
                    />

                    {/* Phase I Wave 3.1 · Lead Discovery (growth+ gated via
                        `lead_discovery_social`). The LinkedIn tab inside the
                        page is further wrapped with a scale+ FeatureGate. */}
                    <Route
                        path="/v2/leads/discovery"
                        element={
                            <GatedRoute featureKey="lead_discovery_social">
                                <V2LeadDiscoveryPage />
                            </GatedRoute>
                        }
                    />

                    {/* Phase I Wave 3.2 · Content Discovery (growth+ gated via
                        `content_discovery_youtube`). The Substack tab inside
                        the page is further wrapped with a scale+ FeatureGate
                        on `content_discovery_newsletter`. */}
                    <Route
                        path="/v2/content/discovery"
                        element={
                            <GatedRoute featureKey="content_discovery_youtube">
                                <V2ContentDiscoveryPage />
                            </GatedRoute>
                        }
                    />

                    {/* Phase I Wave 3.3 · B2C Social Discovery (growth+ gated via
                        `b2c_discovery_twitter`). The TikTok tab inside the page
                        is further gated at scale+ via `b2c_discovery_tiktok`. */}
                    <Route
                        path="/v2/social/discovery"
                        element={
                            <GatedRoute featureKey="b2c_discovery_twitter">
                                <V2B2CDiscoveryPage />
                            </GatedRoute>
                        }
                    />

                    {/* Phase I Wave 3.1 · Admin integrations status · super_admin ONLY. */}
                    <Route
                        path="/v2/admin/integrations"
                        element={
                            <ProtectedRoute>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V2AdminIntegrationsPage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />

                    {_f85_listComingSoonEntries().map((entry) => (
                        <Route
                            key={entry.slug}
                            path={entry.path}
                            element={
                                <ProtectedRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12ComingSoonPage slug={entry.slug} />
                                    </Suspense>
                                </ProtectedRoute>
                            }
                        />
                    ))}

                    {/* Phase VI.5 Wave A · V12 User Shell Unification · legacy↔V12 aliases.
                        11 user-scope real pages mounted under /v2/* (same component as the
                        legacy /notifications, /civilization, /marketplace[/library],
                        /blog, /affiliate, /affiliate/v2 routes). Policy 3a · no 302
                        redirect · bookmarks keep working. Zero-Touch Additive · the
                        legacy mounts above are untouched. Admin-only pages (CreatorStudio,
                        CreatorRoyalties) are NOT aliased here · they stay as placeholder
                        for non-admin and move to the admin shell in Wave B.

                        R2 Issue 3/4 · each Route element is now wrapped in <V12UserLayout>
                        so the alias ALWAYS renders inside the achromatic V12 Workspace
                        shell · the underlying pages' internal public chrome
                        (HeaderV4/FooterV4) and legacy SidebarLayout both no-op on /v2/*
                        URLs for authenticated users (see HeaderV4.jsx + FooterV4.jsx +
                        SidebarLayout.jsx guards).

                        R2 Issue 2 · /marketplace/library (legacy path) is now mounted
                        INSIDE this Wave A block, right before any dynamic marketplace
                        route · guarantees React Router v7 picks the static match. */}
                    <Route path="/marketplace/library"                  element={<ProtectedRoute><V12UserLayout><MarketplaceLibrary /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/notifications"                     element={<ProtectedRoute><V12UserLayout><Notifications /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/ai/civilization"                   element={<ProtectedRoute><V12UserLayout><ModuleGate module="civilization"><Civilization /></ModuleGate></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/marketplace"                       element={<ProtectedRoute><V12UserLayout><Marketplace /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/marketplace/modules"               element={<ProtectedRoute><V12UserLayout><Marketplace /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/marketplace/hub"                   element={<ProtectedRoute><V12UserLayout><Marketplace /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/marketplace/library"               element={<ProtectedRoute><V12UserLayout><MarketplaceLibrary /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/content/blog"                      element={<ProtectedRoute><V12UserLayout><Blog /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/content/articles"                  element={<ProtectedRoute><V12UserLayout><Blog /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/partnership/affiliate"             element={<ProtectedRoute><V12UserLayout><Affiliate /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/partnership/affiliate-v2"          element={<ProtectedRoute><V12UserLayout><AffiliateDashboardV2 /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/v2/partnership/dashboard"             element={<ProtectedRoute><V12UserLayout><AffiliateDashboardV2 /></V12UserLayout></ProtectedRoute>} />
                    {/* END Phase VI.5 Wave A aliases ───────────────────────────────── */}

                    {/* Phase VI.5 Wave B · V12 Admin Shell Unification · 24 legacy↔V12
                        admin aliases. Each Route mounts the SAME real page as the
                        legacy /admin/* path · with <ProtectedRoute adminOnly> +
                        <V12AdminLayout> wrapper so the admin V12 chrome is always
                        present and non-admin users are blocked.
                        Policy 3a stretto · zero 302 redirect · bookmark legacy intatti.
                        Zero-Touch Additive · nessun legacy mount toccato.

                        Dual-frontend check (SEAL §12.4) · NESSUNO di questi path è
                        intercettato dal matcher Next.js SSR · zero exception rules
                        proxy necessarie per Wave B. */}
                    <Route path="/admin/v2/users"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminUsers /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/roles"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseRolesPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/product/modules"        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminModules /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/ai/economy"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminEconomy /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v12/economy/overview"      element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminEconomy /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/ai/governance"          element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminGovernance /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/sys/audit"              element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminAuditLog /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/sys/health"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminSystem /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/sys/tenants"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseTenantsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/leads/engine"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><LeadsListPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/leads/saved"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><SavedLeads /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/outreach/overview"      element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><Outreach /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/outreach/templates"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><OutreachTemplatesAdminPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/outreach/sequences"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><OutreachSequencesAdminPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/outreach/campaigns"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><OutreachCampaigns /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/contatti"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMContactsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/account-aziendali"  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMCompaniesPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/deal"               element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMDealsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/pipeline"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMPipelinePage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/routing"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMRoutingPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/crm/agenti"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMAgentsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/press/releases"         element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><PressReleasesListPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/press/generator"        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><PressGeneratorPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/v2/press/distribution"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><PressDistributionPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    {/* END Phase VI.5 Wave B aliases ───────────────────────────────── */}


                    {/* Phase I Wave 1.23 (F86) · CRM cluster · 6 real pages
                        wired to /api/crm/* with deterministic mock seed. */}
                    <Route path="/v2/crm/contatti" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMContactsPage  /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/crm/aziende"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMCompaniesPage /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/crm/deal"     element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMDealsPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/crm/pipeline" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMPipelinePage  /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/crm/routing"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMRoutingPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/crm/agenti"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMAgentsPage    /></Suspense></ProtectedRoute>} />
                    {/* Phase I Wave 1.28 (F91) · Lead & Outreach cluster · 6 real pages. */}
                    <Route path="/v2/leads"                   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadEnginePage          /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/leads/saved"             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><SavedLeadsPage          /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/outreach"                element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachOverviewPage    /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/outreach/templates"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachTemplatesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/outreach/sequences"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachSequencesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/outreach/campaigns"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachCampaignsPage   /></Suspense></ProtectedRoute>} />
                    {/* Phase I Wave 1.29 (F92) · AI Suite + PRESS + Academy + Community + Partnership mini. */}
                    <Route path="/v2/ai/universe"             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIUniversePage            /></Suspense></ProtectedRoute>} />
                    {/* Phase I Wave 4.1 · RAG + Vector DB (Pinecone lazy + OpenAI embeddings). */}
                    <Route path="/v2/ai/rag"                  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><RAGPage                   /></Suspense></ProtectedRoute>} />
                    {/* Phase I Wave 5 · Lead Enrichment (Apollo + Hunter + NeverBounce · MOCK lazy). */}
                    <Route path="/v2/leads/enrichment"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadEnrichmentPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/ai/badges"               element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIBadgesPage              /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/ai/leaderboard"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AILeaderboardPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/ai/prompts"              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIPromptsPage             /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/press/releases"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressReleasesPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/press/generator"         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressGeneratorPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/press/distribution"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressDistributionPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/academy/courses"         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyCoursesPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/academy/progress"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyProgressPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/academy/certificates"    element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyCertificatesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/community/feed"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityFeedPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/community/groups"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityGroupsPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/community/events"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityEventsPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/community/messages"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityMessagesPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/community/ai"            element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityAIPage           /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/partnership/materials"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PartnershipMaterialsPage  /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/partnership/payout"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PartnershipPayoutPage     /></Suspense></ProtectedRoute>} />

                    {/* Phase I Wave 1.25 (F88) · Account cluster · 7 real
                        pages wired to /api/account/*. Email-preferenze is
                        on a distinct slug to preserve the pre-existing
                        /v2/account/email page (F79 customer receipts). */}
                    <Route path="/v2/account/profilo"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountProfilePage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/crediti"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountCreditsPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/storico-crediti"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountCreditsHistoryPage /></Suspense></ProtectedRoute>} />
                    {/* Hotfix 2026-10 · alias linked from sidebar nav (v12_user_nav.js),
                        CreditPacksPage "Vedi storico transazioni" and checkout result pages.
                        Serves the same AccountCreditsHistoryPage so the URL "/credits/history"
                        no longer falls through to NotFound. */}
                    <Route path="/credits/history"              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountCreditsHistoryPage /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/fatturazione"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountBillingPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/email-preferenze"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountEmailPage          /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/privacy"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountPrivacyPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/v2/account/api-keys"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountAPIKeysPage        /></Suspense></ProtectedRoute>} />

                    {/* Phase I Wave 6.1 · GDPR / CCPA Data Rights Automation. Additive
                        route · the sealed /v2/account/privacy page above is not touched. */}
                    <Route path="/account/privacy"              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V6PrivacyCenterPage       /></Suspense></ProtectedRoute>} />

                    {/* Phase I Wave 6.2 · Security Dashboard (admin+ only).
                        Additive · never touches sealed admin surfaces. */}
                    <Route path="/admin/security"               element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V6SecurityDashboardPage   /></Suspense></ProtectedRoute>} />

                    {/* Phase I Wave 6.3 · Observability Dashboard (admin+ only).
                        Additive · wraps the Wave 6.3 metrics / logs stream. */}
                    <Route path="/admin/observability"          element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V6ObservabilityDashboardPage /></Suspense></ProtectedRoute>} />
                    {/* Phase IX.G · Admin Monitoring Dashboard (aggregato 4 engine Fase IX.G)
                        Hotfix post-Task3: wrap in V12AdminLayout per renderizzare la
                        sidebar admin (altrimenti il cluster "sistema" + item
                        sys-monitoring non appaiono · bug segnalato dal tester). */}
                    <Route path="/admin/monitoring"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-black" />}><V12AdminLayout><AdminMonitoringDashboard /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    {/* PHASE D · observability dashboard dei 14 motori core.
                        Auto-refresh 30s · cache 60s backend · V12AdminLayout già
                        wrapped internamente al component. */}
                    <Route path="/admin/engines/health"         element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-black" />}><AdminEnginesHealthPage /></Suspense></ProtectedRoute>} />
                    {/* Super Admin Hub · /admin/super · launcher super_admin-only.
                        Wrappata in V12AdminLayout per visibilità sidebar admin.
                        Il guard super_admin è duplicato interno al component (Navigate
                        a /app/dashboard?blocked=super_admin_only se role != super_admin). */}
                    <Route path="/admin/super"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-black" />}><V12AdminLayout><SuperAdminHub /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    {/* P0 Pre-launch Pricing · FASE C · super-admin editor per
                        pricing_plans / credit_bundles / pricing_addons / credit_costs.
                        Guard admin a livello route · guard super-admin a livello backend
                        (ogni endpoint 403 se role != super_admin). */}
                    <Route path="/admin/pricing-editor"          element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-black" />}><V12AdminLayout><AdminPricingEditor /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    {/* P0 Pre-launch Pricing · FASE F · Analytics avanzata · gated Scale+ via FeatureGate.
                        Il backend risponde già 403 se il piano non è Scale+, questo wrap offre
                        anche il paywall UI upfront invece del 403 raw. */}
                    <Route path="/app/analytics/advanced"        element={<GatedRoute featureKey="analytics_advanced"><AnalyticsAdvancedPage /></GatedRoute>} />
                    {/* P0 Pre-launch Pricing · FASE G · Automations builder · Growth+ gated.
                        CRUD solo · execution engine in queue → iter 2. */}
                    <Route path="/app/automations/builder"       element={<GatedRoute featureKey="automations_builder"><AutomationsBuilderPage /></GatedRoute>} />

                    {/* Phase I Wave 6.4 · Audit Log admin page (admin+ only).
                        Unified view over Wave 6.1 privacy + Wave 6.2 security
                        audit log + Wave 6.4 retention policies. */}
                    {/* Phase VI.5 Wave B · R1 Issue B · wrapped in V12AdminLayout
                        (pair with /admin/v2/sys/audit · note: componente V6AuditLogPage
                        vs AdminAuditLog è diverso, documentato nel SEAL §12 R1). */}
                    <Route path="/admin/audit-log"              element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><V6AuditLogPage /></V12AdminLayout></Suspense></ProtectedRoute>} />

                    {/* Phase II Wave D.2 · UI chrome translation workbench
                        (admin+ · editor-role accepted via backend gate). */}
                    <Route path="/admin/v9/i18n/strings" element={<Navigate to="/admin/i18n/strings" replace />} />
                    {/* Phase II Wave D.4 · Blog translation workbench (admin only). */}
                    <Route path="/admin/v9/i18n/blog" element={<Navigate to="/admin/i18n/blog" replace />} />
                    {/* Phase II Wave D.5 · Marketplace translation workbench (admin only). */}
                    <Route path="/admin/v9/i18n/marketplace" element={<Navigate to="/admin/i18n/marketplace" replace />} />
                    <Route path="/admin/v9/i18n/banner-analytics" element={<Navigate to="/admin/i18n/banner-analytics" replace />} />
                    {/* Phase II · Wave E.1b · Multi-currency transactional. */}
                    <Route path="/checkout/multi-currency/:asset_slug" element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CheckoutMultiCurrencyPage /></Suspense>} />
                    <Route path="/checkout/success" element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CheckoutSuccessPage /></Suspense>} />
                    <Route path="/checkout/failed"  element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CheckoutFailedPage /></Suspense>} />
                    {/* FASE P1 · Stripe Embedded Checkout · in-site payment flow for plans/suites/cart.
                        Credit-pack purchase keeps the legacy hosted redirect (unchanged above). */}
                    <Route path="/checkout/embedded" element={<Suspense fallback={<div className="min-h-screen bg-[#0B0B0C]" />}><EmbeddedCheckoutPage /></Suspense>} />
                    <Route path="/checkout/return"   element={<Suspense fallback={<div className="min-h-screen bg-[#0B0B0C]" />}><CheckoutReturnPage /></Suspense>} />
                    <Route path="/admin/v9/multi-currency/dashboard" element={<Navigate to="/admin/multi-currency/dashboard" replace />} />
                    {/* Phase II · Wave E.1d · TD7a · Tax-exemption review workbench. */}
                    <Route path="/admin/v9/compliance/exemption-review" element={<Navigate to="/admin/compliance/exemption-review" replace />} />
                    {/* Phase II · Wave E.1e · TD8 · EU OSS workbench (admin + seller self-serve). */}
                    <Route path="/admin/v9/compliance/oss" element={<Navigate to="/admin/compliance/oss" replace />} />
                    <Route path="/account/oss" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OssWorkbenchPage adminMode={false} /></Suspense></ProtectedRoute>} />
                    {/* Phase III · Wave A · Lead Engine Foundation (admin-only). */}
                    {/* Phase VI.5 Wave B · R1 Issue B · wrapped in V12AdminLayout (pair /admin/v2/leads/engine). */}
                    <Route path="/admin/v9/leads" element={<Navigate to="/admin/leads" replace />} />
                    <Route path="/admin/v9/leads/import" element={<Navigate to="/admin/leads/import" replace />} />
                    <Route path="/admin/v9/leads/webhooks" element={<Navigate to="/admin/leads/webhooks" replace />} />
                    <Route path="/admin/v9/leads/scoring/rules" element={<Navigate to="/admin/leads/scoring/rules" replace />} />
                    {/* Phase III · Wave B · Lead Intelligence · additive routes. */}
                    <Route path="/admin/v9/leads/behavior-analytics" element={<Navigate to="/admin/leads/behavior-analytics" replace />} />
                    <Route path="/admin/v9/leads/ml-scoring" element={<Navigate to="/admin/leads/ml-scoring" replace />} />
                    <Route path="/admin/v9/leads/:leadId" element={<Navigate to="/admin/leads/:leadId" replace />} />
                    {/* Phase III · Wave C · Outreach Automation · additive routes. */}
                    {/* Phase VI.5 Wave B · R1 Issue B · wrapped in V12AdminLayout (pair /admin/v2/outreach/sequences). */}
                    <Route path="/admin/v9/outreach/sequences" element={<Navigate to="/admin/outreach/sequences" replace />} />
                    <Route path="/admin/v9/outreach/sequences/:seqId" element={<Navigate to="/admin/outreach/sequences/:seqId" replace />} />
                    {/* Phase VI.5 Wave B · R1 Issue B · wrapped in V12AdminLayout (pair /admin/v2/outreach/templates). */}
                    <Route path="/admin/v9/outreach/templates" element={<Navigate to="/admin/outreach/templates" replace />} />
                    <Route path="/admin/v9/outreach/templates/:tplId" element={<Navigate to="/admin/outreach/templates/:tplId" replace />} />
                    <Route path="/admin/v9/outreach/enrollments" element={<Navigate to="/admin/outreach/enrollments" replace />} />
                    <Route path="/admin/v9/outreach/analytics" element={<Navigate to="/admin/outreach/analytics" replace />} />
                    {/* Phase III · Wave D · Pipeline + Sales Automation · additive routes. */}
                    <Route path="/admin/v9/pipeline/kanban" element={<Navigate to="/admin/pipeline/kanban" replace />} />
                    <Route path="/admin/v9/pipeline/config" element={<Navigate to="/admin/pipeline/config" replace />} />
                    <Route path="/admin/v9/pipeline/analytics" element={<Navigate to="/admin/pipeline/analytics" replace />} />
                    <Route path="/admin/v9/deals/:dealId" element={<Navigate to="/admin/deals/:dealId" replace />} />
                    {/* Phase III · Wave E · CRM Core · additive routes (CrmLayout wraps the full cluster). */}
                    <Route path="/admin/v9/accounts" element={<Navigate to="/admin/accounts" replace />} />
                    <Route path="/admin/v9/accounts/:accountId" element={<Navigate to="/admin/accounts/:accountId" replace />} />
                    <Route path="/admin/v9/contacts" element={<Navigate to="/admin/contacts" replace />} />
                    <Route path="/admin/v9/contacts/:contactId" element={<Navigate to="/admin/contacts/:contactId" replace />} />
                    <Route path="/admin/v9/crm/analytics" element={<Navigate to="/admin/crm/analytics" replace />} />
                    {/* Phase III · Wave F · Data/API Services · additive routes. */}
                    <Route path="/admin/v9/api/keys" element={<Navigate to="/admin/api/keys" replace />} />
                    <Route path="/admin/v9/api/webhooks" element={<Navigate to="/admin/api/webhooks" replace />} />
                    <Route path="/admin/v9/api/webhooks/health" element={<Navigate to="/admin/api/webhooks/health" replace />} />
                    {/* Phase III · Wave G · Psychological No-Escape Funnel · 6 admin routes · CrmLayout chrome. */}
                    <Route path="/admin/v9/funnels/disclosure" element={<Navigate to="/admin/funnels/disclosure" replace />} />
                    <Route path="/admin/v9/funnels/exit-intent" element={<Navigate to="/admin/funnels/exit-intent" replace />} />
                    <Route path="/admin/v9/funnels/gamification" element={<Navigate to="/admin/funnels/gamification" replace />} />
                    <Route path="/admin/v9/funnels/social-proof" element={<Navigate to="/admin/funnels/social-proof" replace />} />
                    <Route path="/admin/v9/funnels/urgency" element={<Navigate to="/admin/funnels/urgency" replace />} />
                    <Route path="/admin/v9/funnels/analytics" element={<Navigate to="/admin/funnels/analytics" replace />} />
                    {/* Phase III · Wave H · A/B Optimizer polish · plan-gated Scale+ · lives under /admin/v9/funnels/ab-optimizer. */}
                    <Route path="/admin/v9/funnels/ab-optimizer" element={<Navigate to="/admin/funnels/ab-optimizer" replace />} />
                    {/* Phase IV · Wave A · Webhooks v2 · developer area · adminOnly · CrmLayout. */}
                    <Route path="/admin/v9/developers/webhooks-v2" element={<Navigate to="/admin/developers/webhooks-v2" replace />} />
                    <Route path="/admin/v9/developers/webhooks-v2/dlq" element={<Navigate to="/admin/developers/webhooks-v2/dlq" replace />} />
                    <Route path="/admin/v9/developers/webhooks-v2/registry" element={<Navigate to="/admin/developers/webhooks-v2/registry" replace />} />
                    <Route path="/admin/v9/developers/webhooks-v2/subscriptions/:id" element={<Navigate to="/admin/developers/webhooks-v2/subscriptions/:id" replace />} />
                    {/* Phase IV · Wave B · Marketplace preset integrations · adminOnly */}
                    <Route path="/admin/v9/marketplace/integrations" element={<Navigate to="/admin/marketplace/integrations" replace />} />
                    <Route path="/admin/v9/marketplace/integrations/:presetKey" element={<Navigate to="/admin/marketplace/integrations/:presetKey" replace />} />
                    <Route path="/admin/v9/marketplace/installations" element={<Navigate to="/admin/marketplace/installations" replace />} />
                    {/* Phase IV · Wave C · Seller onboarding guided wizard · adminOnly */}
                    <Route path="/admin/v9/marketplace/seller/onboarding" element={<Navigate to="/admin/marketplace/seller/onboarding" replace />} />
                    <Route path="/admin/v9/marketplace/seller/profile" element={<Navigate to="/admin/marketplace/seller/profile" replace />} />
                    <Route path="/admin/v9/marketplace/admin/sellers" element={<Navigate to="/admin/marketplace/admin/sellers" replace />} />
                    {/* Phase IV · Wave D · AI Agent marketplace · adminOnly */}
                    <Route path="/admin/v9/marketplace/agents" element={<Navigate to="/admin/marketplace/agents" replace />} />
                    <Route path="/admin/v9/marketplace/agents/catalog" element={<Navigate to="/admin/marketplace/agents/catalog" replace />} />
                    <Route path="/admin/v9/marketplace/agents/templates/:slug" element={<Navigate to="/admin/marketplace/agents/templates/:slug" replace />} />
                    <Route path="/admin/v9/marketplace/agents/creator/studio" element={<Navigate to="/admin/marketplace/agents/creator/studio" replace />} />
                    <Route path="/admin/v9/marketplace/agents/creator/new" element={<Navigate to="/admin/marketplace/agents/creator/new" replace />} />
                    <Route path="/admin/v9/marketplace/agents/creator/edit/:templateId" element={<Navigate to="/admin/marketplace/agents/creator/edit/:templateId" replace />} />
                    <Route path="/admin/v9/marketplace/agents/creator/royalties" element={<Navigate to="/admin/marketplace/agents/creator/royalties" replace />} />
                    <Route path="/admin/v9/marketplace/agents/admin/review" element={<Navigate to="/admin/marketplace/agents/admin/review" replace />} />
                    {/* Phase IV · Wave E · Federated Search · adminOnly */}
                    <Route path="/admin/v9/search/results" element={<Navigate to="/admin/search/results" replace />} />
                    <Route path="/admin/v9/search/admin" element={<Navigate to="/admin/search/admin" replace />} />
                    {/* Phase IV · Wave F · Advanced Analytics Dashboard · adminOnly */}
                    <Route path="/admin/v9/analytics/advanced" element={<Navigate to="/admin/analytics/advanced" replace />} />
                    {/* Wave RF.R1 · redirect top-level /admin/v9/{analytics,users,press} per bookmark coverage */}
                    <Route path="/admin/v9/analytics" element={<Navigate to="/admin/analytics" replace />} />
                    <Route path="/admin/v9/users" element={<Navigate to="/admin/users" replace />} />
                    <Route path="/admin/v9/press" element={<Navigate to="/admin/press/releases" replace />} />

                    {/* Phase VII · Wave SEO.AUTO · 3 V12 admin SEO pages (indexing · GSC · Bing). */}
                    <Route path="/admin/seo/indexing" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminSEOIndexingPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/gsc" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminGSCPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/bing" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminBingPage /></Suspense></ProtectedRoute>} />
                    {/* Phase IV · Wave G · Enterprise RBAC/SSO · adminOnly */}
                    <Route path="/admin/v9/enterprise" element={<Navigate to="/admin/enterprise" replace />} />
                    {/* Phase VI.5 Wave B · R1 Issue B · CrmLayout → V12AdminLayout (pair /admin/v2/sys/tenants). */}
                    <Route path="/admin/v9/enterprise/tenants" element={<Navigate to="/admin/enterprise/tenants" replace />} />
                    <Route path="/admin/v9/enterprise/sso" element={<Navigate to="/admin/enterprise/sso" replace />} />
                    {/* Phase VI.5 Wave B · R1 Issue B · CrmLayout → V12AdminLayout (pair /admin/v2/roles). */}
                    <Route path="/admin/v9/enterprise/roles" element={<Navigate to="/admin/enterprise/roles" replace />} />
                    <Route path="/admin/v9/enterprise/users" element={<Navigate to="/admin/enterprise/users" replace />} />
                    {/* Phase V · Wave A · Audit denials log · adminOnly */}
                    <Route path="/admin/v9/enterprise/audit" element={<Navigate to="/admin/enterprise/audit" replace />} />
                    {/* Phase V · Wave B · Multi-tenant hard-cutover · adminOnly */}
                    <Route path="/admin/v9/enterprise/migration" element={<Navigate to="/admin/enterprise/migration" replace />} />
                    {/* Phase V · Wave C · Reseller mode + pricing tier · adminOnly */}
                    <Route path="/admin/v9/reseller" element={<Navigate to="/admin/reseller" replace />} />
                    <Route path="/admin/v9/reseller/subtenants" element={<Navigate to="/admin/reseller/subtenants" replace />} />
                    <Route path="/admin/v9/reseller/commissions" element={<Navigate to="/admin/reseller/commissions" replace />} />
                    {/* Phase V · Wave D · Branding + custom domains · adminOnly */}
                    <Route path="/admin/v9/branding" element={<Navigate to="/admin/branding" replace />} />
                    <Route path="/admin/v9/branding/custom-domains" element={<Navigate to="/admin/branding/custom-domains" replace />} />
                    {/* Phase VI · Wave A · Blog engine · admin + public */}
                    <Route path="/admin/v9/blog" element={<Navigate to="/admin/blog" replace />} />
                    <Route path="/admin/v9/blog/new" element={<Navigate to="/admin/blog/new" replace />} />
                    <Route path="/admin/v9/blog/:postId" element={<Navigate to="/admin/blog/:postId" replace />} />
                    <Route path="/blog"                                                 element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PublicBlogIndexPage /></Suspense>} />
                    <Route path="/blog/:slug"                                           element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PublicBlogPostPage /></Suspense>} />
                    {/* Phase VI · Wave B · SEO Master System · admin + public */}
                    <Route path="/admin/v9/seo/keywords" element={<Navigate to="/admin/seo/keywords" replace />} />
                    <Route path="/admin/v9/seo/topics" element={<Navigate to="/admin/seo/topics" replace />} />
                    <Route path="/admin/v9/seo/pages" element={<Navigate to="/admin/seo/pages" replace />} />
                    <Route path="/admin/v9/seo/pages/:pageId" element={<Navigate to="/admin/seo/pages/:pageId" replace />} />
                    <Route path="/pages/:slug"                                          element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><SeoPublicPage /></Suspense>} />
                    {/* Phase VI · Wave C · Affiliate Program · routes renamed 2026-02-28 (Bug #3) */}
                    <Route path="/admin/v9/affiliate/dashboard" element={<Navigate to="/admin/affiliate/dashboard" replace />} />
                    <Route path="/admin/v9/affiliate/materials" element={<Navigate to="/admin/affiliate/materials" replace />} />
                    <Route path="/admin/v9/affiliate/payouts" element={<Navigate to="/admin/affiliate/payouts" replace />} />
                    <Route path="/admin/v9/affiliate/review" element={<Navigate to="/admin/affiliate/review" replace />} />
                    {/* Wave VI.C hotfix 2026-02-28 · Bug #1 · public /r/:code SPA redirect to API endpoint (preserves UTM + dest). */}
                    <Route path="/r/:code"                                              element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AffiliateRedirect /></Suspense>} />
                    {/* Phase VI · Wave D · Social Automation (full MOCK) · 2026-02-28. */}
                    <Route path="/admin/v9/social/connections" element={<Navigate to="/admin/social/connections" replace />} />
                    <Route path="/admin/v9/social/composer" element={<Navigate to="/admin/social/composer" replace />} />
                    <Route path="/admin/v9/social/calendar" element={<Navigate to="/admin/social/calendar" replace />} />
                    <Route path="/admin/v9/social/history" element={<Navigate to="/admin/social/history" replace />} />
                    {/* Phase VI · Wave E · Press / Distribution. */}
                    <Route path="/admin/v9/press/releases" element={<Navigate to="/admin/press/releases" replace />} />
                    <Route path="/admin/v9/press/releases/:releaseId" element={<Navigate to="/admin/press/releases/:releaseId" replace />} />
                    <Route path="/admin/v9/press/contacts" element={<Navigate to="/admin/press/contacts" replace />} />
                    <Route path="/admin/v9/press/mentions" element={<Navigate to="/admin/press/mentions" replace />} />
                    <Route path="/press"                                                element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PublicPressRoomPage /></Suspense>} />
                    <Route path="/press/media-kit"                                      element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PublicMediaKitPage /></Suspense>} />
                    <Route path="/press/:slug"                                          element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PublicPressReleasePage /></Suspense>} />
                    {/* Phase VI · Wave F · Referral Viral Loop (closes Phase VI). */}
                    <Route path="/admin/v9/referral/invite" element={<Navigate to="/admin/referral/invite" replace />} />
                    <Route path="/admin/v9/referral/dashboard" element={<Navigate to="/admin/referral/dashboard" replace />} />
                    <Route path="/admin/v9/referral/leaderboard" element={<Navigate to="/admin/referral/leaderboard" replace />} />
                    <Route path="/signup"                                               element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><ReferralSignupPrefillPage /></Suspense>} />
                    <Route path="/developers" element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><DevelopersPage /></Suspense>} />
                    {/* Phase II · Wave E.1c · Rate-limiter admin observability. */}
                    <Route path="/admin/v9/system/rate-limiter" element={<Navigate to="/admin/system/rate-limiter" replace />} />
                    {/* Phase II · Wave E.1c.2 · TD1 · Public compliance VAT page. */}
                    <Route path="/compliance/vat/:countryCode" element={<Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><ComplianceVatPage /></Suspense>} />

                    {/* Phase I Wave 1.24 (F87) · ~40 admin placeholder routes.
                        Mirrors the F85 user pattern but with `adminOnly`
                        guard and V12AdminLayout chrome. See
                        `data/v12_admin_coming_soon_registry.js`. */}
                    {_f87_listAdminComingSoonEntries().map((entry) => (
                        <Route
                            key={entry.slug}
                            path={entry.path}
                            element={
                                <ProtectedRoute adminOnly>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12AdminComingSoonPage slug={entry.slug} />
                                    </Suspense>
                                </ProtectedRoute>
                            }
                        />
                    ))}
                    {/* Phase I Wave 1.27 (F90) · SPA escape · /admin/v2/legacy/:slug
                        redirects to the classic admin route via <Navigate replace>,
                        so the user never gets a browser full reload. */}
                    <Route
                        path="/admin/v2/legacy/:slug"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12AdminLegacyRedirect />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/checkout/success"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12CheckoutSuccessPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/checkout/cancel"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12CheckoutCancelPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/account/email"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12EmailResendPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/ai/social/twitter"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12TwitterIntegrationShell />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/ai/social/linkedin"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12LinkedInIntegrationShell />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/seo/gsc"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12GSCConnectPage />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/v2/seo/bing"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12BingDashboardPage />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase H+1 Mini-Wave 6 · Admin Cockpit Extension · placeholder catch-all */}
                    <Route
                        path="/admin/v12/:cluster/:item"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminPlaceholderPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />

                    {/* Phase VI.5 Wave C · V12 Super Admin shell · 7 route /sa/* */}
                    <Route path="/sa" element={<Navigate to="/sa/dashboard" replace />} />
                    <Route
                        path="/sa/dashboard"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminDashboardPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/tenants"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminTenantsListPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/tenants/:id"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminTenantDetailPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/enterprise"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminEnterprisePage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/resellers"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminResellersPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/analytics"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminAnalyticsPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/branding"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminBrandingPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />
                    <Route
                        path="/sa/alerts"
                        element={
                            <V12SuperAdminOnlyRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D]" />}>
                                    <V12SuperAdminAlertsPage />
                                </Suspense>
                            </V12SuperAdminOnlyRoute>
                        }
                    />

                    {/* ===== Phase VI.5 · Wave RF.1 · nuovi canonical URL · additive · zero touch legacy ===== */}
                    {/* /v2/* → /app/* (user shell) · stesso componente · stesso wrapper */}
                    <Route
                        path="/app/dashboard"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12UserDashboardPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/referral"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V2ReferralPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/admin/dev-toolkit"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V2AdminDevToolkitPage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/credits"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    {/* Wave VII.A R5a FIX 1 · Unifica flow checkout.
                                        La pagina legacy CreditPackPurchasePage usava Phase I
                                        /api/billing/* endpoints disallineati dai Wave VII.A
                                        packs · click "Acquista" non navigava. Rimappiamo a
                                        V7ACreditPacksPage (stessa pagina di /credits/packs e
                                        /pricing) che naviga correttamente a /checkout?pack=... */}
                                    <V7ACreditPacksPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/subscription"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    {/* Wave VII.A R5 · My Subscription management page.
                                        Rimpiazza la legacy UnifiedSubscriptionPage (Phase I
                                        Wave 1.21 F84) · nuova UI V12 con status/cancel/
                                        reactivate/portal + billing history dalla webhook cache. */}
                                    <V7AMySubscriptionPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/subscription/calc"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <UnifiedSubscriptionPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/app/billing"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <UnifiedSubscriptionPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/app/seo/dashboard"
                        element={
                            <GatedRoute featureKey="seo_master_base">
                                <V2SeoMasterDashboardShell />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/blog/engine"
                        element={
                            <GatedRoute featureKey="blog_engine_10">
                                <V2BlogEngineShell />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/ai/agents"
                        element={
                            <GatedRoute featureKey="ai_agents_preconfigured">
                                <V2AIAgentsShell />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/leads/discovery"
                        element={
                            <GatedRoute featureKey="lead_discovery_social">
                                <V2LeadDiscoveryPage />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/content/discovery"
                        element={
                            <GatedRoute featureKey="content_discovery_youtube">
                                <V2ContentDiscoveryPage />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/social/discovery"
                        element={
                            <GatedRoute featureKey="b2c_discovery_twitter">
                                <V2B2CDiscoveryPage />
                            </GatedRoute>
                        }
                    />
                    <Route
                        path="/app/admin/integrations"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V2AdminIntegrationsPage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* Wave X.B · Admin Observability dashboard. */}
                    <Route
                        path="/app/admin/observability"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <ObservabilityDashboard />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/app/notifications"                     element={<ProtectedRoute><V12UserLayout><Notifications /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/ai/civilization"                   element={<ProtectedRoute><V12UserLayout><ModuleGate module="civilization"><Civilization /></ModuleGate></V12UserLayout></ProtectedRoute>} />
                    {/* MP.7 Hotfix R7 · legacy /app/marketplace → /marketplace/v10 canonical.
                        Mantiene retrocompatibilità con bookmark utente, link esterni, e
                        qualunque reference legacy a questo path. */}
                    <Route path="/app/marketplace"                       element={<Navigate to="/marketplace/v10" replace />} />
                    <Route path="/app/marketplace/modules"               element={<ProtectedRoute><V12UserLayout><Marketplace /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/marketplace/hub"                   element={<ProtectedRoute><V12UserLayout><Marketplace /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/marketplace/library"               element={<ProtectedRoute><V12UserLayout><MarketplaceLibrary /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/content/blog"                      element={<ProtectedRoute><V12UserLayout><Blog /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/content/articles"                  element={<ProtectedRoute><V12UserLayout><Blog /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/partnership/affiliate"             element={<ProtectedRoute><V12UserLayout><Affiliate /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/partnership/affiliate-v2"          element={<ProtectedRoute><V12UserLayout><AffiliateDashboardV2 /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/partnership/dashboard"             element={<ProtectedRoute><V12UserLayout><AffiliateDashboardV2 /></V12UserLayout></ProtectedRoute>} />
                    <Route path="/app/crm/contatti" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMContactsPage  /></Suspense></ProtectedRoute>} />
                    <Route path="/app/crm/aziende"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMCompaniesPage /></Suspense></ProtectedRoute>} />
                    <Route path="/app/crm/deal"     element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMDealsPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/app/crm/pipeline" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMPipelinePage  /></Suspense></ProtectedRoute>} />
                    <Route path="/app/crm/routing"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMRoutingPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/app/crm/agenti"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CRMAgentsPage    /></Suspense></ProtectedRoute>} />
                    <Route path="/app/leads"                   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadEnginePage          /></Suspense></ProtectedRoute>} />
                    <Route path="/app/leads/saved"             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><SavedLeadsPage          /></Suspense></ProtectedRoute>} />
                    <Route path="/app/outreach"                element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachOverviewPage    /></Suspense></ProtectedRoute>} />
                    <Route path="/app/outreach/templates"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachTemplatesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/app/outreach/sequences"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachSequencesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/app/outreach/campaigns"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachCampaignsPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/app/ai/universe"             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIUniversePage            /></Suspense></ProtectedRoute>} />
                    <Route path="/app/ai/rag"                  element={<GatedRoute featureKey="rag_chat"><RAGPage                   /></GatedRoute>} />
                    <Route path="/app/leads/enrichment"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadEnrichmentPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/leads/apollo-live"       element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><ApolloLivePage            /></Suspense></ProtectedRoute>} />
                    <Route path="/marketplace/creator/workflows/new" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CreatorPublishWorkflowPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/fee-bands"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AdminFeeBandsPage      /></Suspense></ProtectedRoute>} />
                    <Route path="/app/ai/badges"               element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIBadgesPage              /></Suspense></ProtectedRoute>} />
                    <Route path="/app/ai/leaderboard"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AILeaderboardPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/app/ai/prompts"              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AIPromptsPage             /></Suspense></ProtectedRoute>} />
                    <Route path="/app/press/releases"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressReleasesPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/app/press/generator"         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressGeneratorPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/press/distribution"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PressDistributionPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/app/academy/courses"         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyCoursesPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/academy/progress"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyProgressPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/app/academy/certificates"    element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AcademyCertificatesPage   /></Suspense></ProtectedRoute>} />
                    <Route path="/app/community/feed"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityFeedPage         /></Suspense></ProtectedRoute>} />
                    <Route path="/app/community/groups"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityGroupsPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/app/community/events"        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityEventsPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/app/community/messages"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityMessagesPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/app/community/ai"            element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CommunityAIPage           /></Suspense></ProtectedRoute>} />
                    <Route path="/app/partnership/materials"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PartnershipMaterialsPage  /></Suspense></ProtectedRoute>} />
                    <Route path="/app/partnership/payout"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><PartnershipPayoutPage     /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/profilo"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountProfilePage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/crediti"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountCreditsPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/storico-crediti"   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountCreditsHistoryPage /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/fatturazione"      element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountBillingPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/email-preferenze"  element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountEmailPage          /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/privacy"           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountPrivacyPage        /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/api-keys"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountAPIKeysPage        /></Suspense></ProtectedRoute>} />
                    {/* Wave X.C.3 · 2FA security self-service · must stay accessible even when backend 2FA gate is active. */}
                    <Route path="/app/account/security"          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountSecurityPage       /></Suspense></ProtectedRoute>} />
                    <Route path="/app/account/sicurezza"         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AccountSecurityPage       /></Suspense></ProtectedRoute>} />
                    <Route
                        path="/app/checkout/success"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12CheckoutSuccessPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/checkout/cancel"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12CheckoutCancelPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/account/email"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12EmailResendPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/ai/social/twitter"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12TwitterIntegrationShell />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/ai/social/linkedin"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12LinkedInIntegrationShell />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/seo/gsc"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12GSCConnectPage />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/app/seo/bing"
                        element={
                            <ProtectedRoute>
                                <V12AdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12BingDashboardPage />
                                    </Suspense>
                                </V12AdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* /admin/v2/* → /admin/* (admin shell canonical) · pair strict */}
                    <Route
                        path="/admin/dashboard"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12AdminDashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/social/dispatch-log-live"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SSEDispatchLogPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/social/thread"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12ThreadAdminPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/social/media"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12MediaAdminPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/admin/roles"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseRolesPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/product/modules"        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminModules /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/ai/economy"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminEconomy /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/ai/governance"          element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminGovernance /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/sys/audit"              element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminAuditLog /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/sys/health"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminSystem /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/sys/tenants"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseTenantsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/engine"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><LeadsListPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/saved"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><SavedLeads /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/overview"      element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><Outreach /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/templates"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><OutreachTemplatesAdminPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/campaigns"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><OutreachCampaigns /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/contatti"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMContactsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/account-aziendali"  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMCompaniesPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/deal"               element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMDealsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/agenti"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><CRMAgentsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/legacy/:slug"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12AdminLegacyRedirect />
                            </ProtectedRoute>
                        }
                    />
                    {/* /admin/v9/* → /admin/* (admin shell canonical · componente v9 più nuovo) */}
                    <Route path="/admin/i18n/strings"        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><I18nStringsWorkbenchPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/i18n/blog"           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><BlogI18nWorkbenchPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/i18n/marketplace"    element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><MarketplaceI18nWorkbenchPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/i18n/banner-analytics" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><BannerAnalyticsPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/multi-currency/dashboard" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><MultiCurrencyDashboardPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/compliance/exemption-review" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><ExemptionReviewPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/compliance/oss" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OssWorkbenchPage adminMode={true} /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/import" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadsCsvImportPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/webhooks" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadsWebhooksPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/scoring/rules" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadsScoringRulesPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/behavior-analytics" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadsBehaviorAnalyticsPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/ml-scoring" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadsMlScoringPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/leads/:leadId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><LeadDetailPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/sequences/:seqId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachSequenceBuilderPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/templates/:tplId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachTemplateEditorPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/enrollments" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachEnrollmentsPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/analytics" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><OutreachAnalyticsPage /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/pipeline/kanban" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><PipelineKanbanPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/pipeline/config" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><PipelineConfigPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/pipeline/analytics" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><RevenueAnalyticsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/deals/:dealId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><DealDetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/accounts" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AccountsListPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/accounts/:accountId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AccountDetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/contacts" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ContactsListPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/contacts/:contactId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ContactDetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/analytics" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><CrmAnalyticsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/api/keys" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ApiKeysPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/api/webhooks" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhooksPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/api/webhooks/health" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhookHealthPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/disclosure"    element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelDisclosurePage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/exit-intent"   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelExitIntentPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/gamification"  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelGamificationPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/social-proof"  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelSocialProofPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/urgency"       element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelUrgencyPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/analytics"     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><FunnelAnalyticsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/funnels/ab-optimizer"  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AbOptimizerPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/developers/webhooks-v2"                     element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhooksV2Page /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/developers/webhooks-v2/dlq"                 element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhooksV2DlqPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/developers/webhooks-v2/registry"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhooksV2RegistryPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/developers/webhooks-v2/subscriptions/:id"   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><WebhooksV2DetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/integrations"                   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><IntegrationsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/integrations/:presetKey"        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><IntegrationDetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/installations"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><MyInstallationsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/seller/onboarding"              element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SellerOnboardingPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/seller/profile"                 element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SellerProfilePage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/admin/sellers"                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AdminSellerReviewPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents"                         element={<Navigate to="/admin/v9/marketplace/agents/catalog" replace />} />
                    <Route path="/admin/marketplace/agents/catalog"                 element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AgentCatalogPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/templates/:slug"         element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AgentTemplateDetailPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/creator/studio"          element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><CreatorStudioPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/creator/new"             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><CreatorTemplateEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/creator/edit/:templateId" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><CreatorTemplateEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/creator/royalties"       element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><CreatorRoyaltiesPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/marketplace/agents/admin/review"            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AdminAgentReviewPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/search/results"                             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SearchResultsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/search/admin"                               element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AdminSearchStatusPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/analytics/advanced"                         element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AdvancedAnalyticsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise"                                 element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><EnterpriseHomePage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/tenants"                         element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseTenantsPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/sso"                             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><EnterpriseSsoConfigsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/roles"                           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><EnterpriseRolesPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/users"                           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><EnterpriseUsersPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/audit"                           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><EnterpriseAuditPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/enterprise/migration"                       element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><MigrationHardCutoverPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/reseller"                                   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ResellerDashboardPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/reseller/subtenants"                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ResellerSubtenantsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/reseller/commissions"                       element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ResellerCommissionsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/branding"                                   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><BrandingEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/branding/custom-domains"                    element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><BrandingCustomDomainsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/custom-domains"                             element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><AdminCustomDomainsPageHB /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/blog/new"                                    element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><BlogEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/blog/:postId"                                element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><BlogEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/keywords"                                element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SeoKeywordsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/topics"                                  element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SeoTopicsQueuePage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/pages"                                   element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SeoPagesListPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/seo/pages/:pageId"                           element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SeoPageEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/affiliate/dashboard"                         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AffiliateDashboardPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/affiliate/materials"                         element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AffiliateMaterialsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/affiliate/payouts"                           element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AffiliatePayoutsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/affiliate/review"                            element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><AdminAffiliateReviewPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/social/connections"                          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SocialConnectionsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/social/composer"                             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SocialComposerPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/social/calendar"                             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SocialCalendarPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/social/history"                              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><SocialHistoryPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/press/releases/:releaseId"                   element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><PressReleaseEditorPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/press/contacts"                              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><PressContactsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/press/mentions"                              element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><PressMentionsPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/referral/invite"                             element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ReferralInvitePage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/referral/dashboard"                          element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ReferralDashboardPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/referral/leaderboard"                        element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><CrmLayout><ReferralLeaderboardPage /></CrmLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/system/rate-limiter" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><RateLimiterAdminPage /></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/blog/composer"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <PostComposerPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/blog/composer/:postId"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <PostComposerPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/blog/moderation"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <ModerationQueuePage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/blog/calendar"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <CalendarPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/blog/clusters"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <ClustersPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/seo/sitemap-stats"
                        element={
                            <EditorialRouteGuard adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <SitemapStatsPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    <Route
                        path="/admin/content-gen/jobs"
                        element={
                            <EditorialRouteGuard>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <ContentGenJobsPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />
                    {/* ===== Wave RF.1 END ===== */}


                    {/* Phase I Wave 1.10 · Enterprise Contact Form (user scope + admin inbox) */}
                    <Route
                        path="/contact/enterprise"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12EnterpriseContactPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contact/enterprise/thank-you"
                        element={
                            <ProtectedRoute>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12EnterpriseThankYouPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v12/enterprise-requests"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminEnterpriseRequestsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 1.11 · Admin Analytics Tenants (super_admin only) */}
                    <Route
                        path="/admin/v12/analytics/tenants"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12AdminAnalyticsTenantsPage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 1 · Go Live Prep · Admin Live Mode flip (super_admin only) */}
                    <Route
                        path="/admin/v12/billing/live-mode"
                        element={
                            <ProtectedRoute adminOnly>
                                <V12SuperAdminOnlyRoute>
                                    <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                        <V12AdminLiveModePage />
                                    </Suspense>
                                </V12SuperAdminOnlyRoute>
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase I Wave 2 · Social & Distribution · 4 admin pages */}
                    <Route
                        path="/admin/v12/social/reddit"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminSocialRedditPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v12/social/medium"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminSocialMediumPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v12/social/quora"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminSocialQuoraPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/v12/social/substack"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <V12AdminSocialSubstackPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/system-settings"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminSystemSettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/feature-flags"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminSystemSettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/webhooks"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminWebhooksPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/analytics/lock-funnel"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminLockFunnelPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* F10 Phase 0 · Pipeline Autonoma monitoring (lazy-loaded) */}
                    <Route
                        path="/admin/pipeline"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="p-10 text-sm text-[var(--color-text-secondary)]">Loading pipeline…</div>}>
                                    <AdminPipelinePage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* F13.5 · P1 · Admin UI for contact / waitlist / email-queue (lazy-loaded) */}
                    <Route
                        path="/admin/contact-submissions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="p-10 text-sm text-[var(--color-text-secondary)]">Loading…</div>}>
                                    <AdminContactSubmissionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/waitlist"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="p-10 text-sm text-[var(--color-text-secondary)]">Loading…</div>}>
                                    <AdminWaitlistPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/email-queue"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="p-10 text-sm text-[var(--color-text-secondary)]">Loading…</div>}>
                                    <AdminEmailQueuePage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/pricing"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminPlaceholderPage
                                    title="Gestione Pricing"
                                    description="Editor dei plans + credit_packs usati da /api/pricing/public."
                                    hint="Oggi i plans si editano direttamente in Mongo (collection plans)."
                                />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog/:id"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminBlogEdit />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase 8A — Admin panel: autonomy, analytics, audit */}
                    <Route
                        path="/admin/autonomy"
                        element={<ProtectedRoute adminOnly><AdminAutonomy /></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/analytics"
                        element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>}
                    />
                    {/* F9.3 · WAVE 3 pre-cutover monitoring dashboard — Landing V3 funnel. */}
                    <Route
                        path="/admin/analytics/v3-funnel"
                        element={<ProtectedRoute adminOnly><AdminV3Funnel /></ProtectedRoute>}
                    />
                    {/* Phase VI.5 Wave B · R1 Issue B · dead-code dup di /admin/audit-log
                        (il match vincente è alla line ~1938 con V6AuditLogPage). Lascio
                        wrappato V12AdminLayout per coerenza se mai venisse promosso. */}
                    <Route
                        path="/admin/audit-log"
                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminAuditLog /></V12AdminLayout></Suspense></ProtectedRoute>}
                    />
                    {/* Phase 8B — User Mgmt + System Panel + Affiliate enhance */}
                    {/* Phase VI.5 Wave B · R1 Issue B · legacy /admin/users + /admin/system
                        + /admin/governance wrappati in V12AdminLayout (pair strict con
                        /admin/v2/users · /admin/v2/sys/health · /admin/v2/ai/governance). */}
                    <Route
                        path="/admin/users"
                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminUsers /></V12AdminLayout></Suspense></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/users/:id"
                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminUserDetail /></V12AdminLayout></Suspense></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/system"
                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminSystem /></V12AdminLayout></Suspense></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/affiliate"
                        element={<ProtectedRoute adminOnly><AdminAffiliate /></ProtectedRoute>}
                    />
                    {/* Phase 9 — Governance AI (unified queue + fraud + rollback) */}
                    <Route
                        path="/admin/governance"
                        element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}><V12AdminLayout><AdminGovernance /></V12AdminLayout></Suspense></ProtectedRoute>}
                    />
                    {/* Phase 11 V50 — Self-Optimization meta-intelligence report */}
                    <Route
                        path="/admin/v50"
                        element={<ProtectedRoute adminOnly><AdminV50 /></ProtectedRoute>}
                    />
                    {/* Phase 12 Email Engine V10 — meta-intelligence email advisor */}
                    <Route
                        path="/admin/email-engine"
                        element={<ProtectedRoute adminOnly><EmailEngineDashboard /></ProtectedRoute>}
                    />
                    {/* Phase 13 SEO Engine V10 — meta-intelligence SEO advisor */}
                    <Route
                        path="/admin/seo-engine"
                        element={<ProtectedRoute adminOnly><SeoEngineDashboard /></ProtectedRoute>}
                    />
                    {/* Phase 14 SEO Apply Layer — REAL mutations (opt-in, whitelist, 24h rollback) */}
                    <Route
                        path="/admin/seo-apply"
                        element={<ProtectedRoute adminOnly><SeoApplyDashboard /></ProtectedRoute>}
                    />
                    {/* Phase 15 Email Send Layer — dispatch/schedule/retract (sandbox default) */}
                    <Route
                        path="/admin/email-send"
                        element={<ProtectedRoute adminOnly><EmailSendDashboard /></ProtectedRoute>}
                    />
                    {/* Phase 16b Affiliate v2 — wrapper over Phase 3 (self + admin) */}
                    <Route
                        path="/affiliate/v2"
                        element={<ProtectedRoute><AffiliateDashboardV2 /></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/affiliate-v2"
                        element={<ProtectedRoute adminOnly><AdminAffiliateV2 /></ProtectedRoute>}
                    />
                    {/* Phase 16c Marketplace v3 — creator studio + admin moderation */}
                    <Route
                        path="/marketplace/creator-studio"
                        element={<ProtectedRoute><CreatorDashboardV3 /></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/marketplace-v3"
                        element={<ProtectedRoute adminOnly><AdminMarketplaceV3 /></ProtectedRoute>}
                    />
                    {/* Phase 24e · MarketplaceEngine V4 — paid assets + creator earnings */}
                    <Route path="/marketplace/asset/:id" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />
                    <Route path="/creator/:id/store" element={<ProtectedRoute><CreatorStore /></ProtectedRoute>} />
                    <Route path="/account/creator/earnings" element={<ProtectedRoute><CreatorEarnings /></ProtectedRoute>} />
                    <Route path="/admin/marketplace-v24e" element={<ProtectedRoute adminOnly><AdminMarketplaceV24e /></ProtectedRoute>} />
                    {/* Phase 25b · Marketplace V2 Discovery + Cart */}
                    <Route path="/marketplace-hub" element={<ProtectedRoute><MarketplaceHub /></ProtectedRoute>} />
                    <Route path="/cart" element={<ProtectedRoute><V12UserLayout><MP1CartPage /></V12UserLayout></ProtectedRoute>} />
                    {/* FASE P2 · deprecate /cart/success in favour of the unified
                        /checkout/return outcome page · session_id is forwarded.
                        Legacy FASE M polling target still available via the
                        `:preview_id` variant below for pre-FASE-P2 URLs. */}
                    <Route
                        path="/cart/success"
                        element={<CartSuccessRedirect />}
                        data-testid="route-cart-success-deprecated"
                    />
                    <Route path="/cart/success/:preview_id" element={<ProtectedRoute><CartSuccess /></ProtectedRoute>} />
                    {/* Phase 26 · Video Engine V1 — public SEO pages + admin */}
                    <Route path="/video/:id" element={<VideoDetail />} />
                    <Route path="/video/:id/embed" element={<VideoEmbed />} />
                    <Route path="/videos" element={<VideosBrowse />} />
                    <Route path="/videos/pack/:pack_id" element={<VideoPackDetail />} />
                    <Route
                        path="/admin/videos"
                        element={
                            <ProtectedRoute adminOnly>
                                <AdminVideos />
                            </ProtectedRoute>
                        }
                    />
                    {/* Phase 17 · Public FAQ — no auth (public for SEO) */}
                    <Route path="/faq" element={<PublicFAQ />} />
                    {/* Public unsubscribe confirmation page (no auth) */}
                    <Route path="/unsubscribed" element={<Unsubscribed />} />
                    {/* Phase 10 V30 — Mini-landings admin + Schedule + public landing */}
                    <Route
                        path="/admin/mini-landings"
                        element={<ProtectedRoute adminOnly><AdminMiniLandings /></ProtectedRoute>}
                    />
                    <Route
                        path="/admin/schedule"
                        element={<ProtectedRoute adminOnly><AdminSchedule /></ProtectedRoute>}
                    />
                    <Route path="/landing/:slug" element={<MiniLandingPage />} />

                    {/* ===================================================
                        F12 · Header + Footer refactor (minimal premium-dark)
                        New placeholder routes referenced by HeaderV4 / FooterV4.
                        Each wrapped in <MainLayout> which renders HeaderV4
                        + FooterV4 (REACT_APP_HEADER_V4_ENABLED kill-switch).
                        ================================================== */}

                    {/* Product column — F13 Wave B marketing pages */}
                    <Route path="/features" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12FeaturesPage /></Suspense>} />
                    <Route path="/ecosystems" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12EcosystemsPage /></Suspense>} />
                    <Route path="/ai-universe" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12AiUniversePage /></Suspense>} />
                    <Route path="/ai-civilization" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12AiCivilizationPage /></Suspense>} />
                    <Route path="/badge-system" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12BadgeSystemPage /></Suspense>} />

                    {/* Resources column — F13 Wave B docs & resources */}
                    <Route path="/docs" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12DocsPage /></Suspense>} />
                    {/* Phase IX.G.2 · auto-generated user docs */}
                    <Route path="/app/docs" element={<UserAutoDocsPage />} />
                    {/* Phase VII.C.2 · Creator payouts */}
                    <Route path="/app/creator/earnings" element={<CreatorEarningsPage />} />
                    <Route path="/app/creator/connect/return" element={<CreatorConnectReturnPage />} />
                    <Route path="/app/creator/connect/refresh" element={<CreatorConnectReturnPage />} />
                    <Route path="/docs/api" element={<MainLayout><ApiReferencePage /></MainLayout>} />
                    <Route path="/docs/webhooks" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12DocsWebhooksPage /></Suspense>} />
                    <Route path="/docs/credits" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12DocsCreditsPage /></Suspense>} />
                    <Route path="/docs/limits" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12DocsLimitsPage /></Suspense>} />
                    <Route path="/seo" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12SeoPage /></Suspense>} />
                    <Route path="/academy" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12AcademyPage /></Suspense>} />

                    {/* Legal column */}
                    <Route path="/privacy" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12PrivacyPage /></Suspense>} />
                    <Route path="/terms" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12TermsPage /></Suspense>} />
                    <Route path="/cookies" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12CookiesPage /></Suspense>} />
                    <Route path="/compliance" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12CompliancePage /></Suspense>} />
                    <Route path="/sla" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12SlaPage /></Suspense>} />
                    <Route path="/onboarding" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12OnboardingPage /></Suspense>} />

                    {/* F13 Wave A · Contact page (Nocturna Network Ltd) */}
                    <Route path="/contact" element={<Suspense fallback={<div className="min-h-screen bg-black" />}><V12ContactPage /></Suspense>} />

                    {/* Account column aliases → delegate to existing protected routes */}
                    <Route path="/register" element={<LoginRoute tab="signup" />} />
                    <Route path="/dashboard/billing" element={<Navigate to="/billing" replace />} />
                    <Route path="/dashboard/credits" element={<Navigate to="/credits" replace />} />
                    <Route path="/dashboard/campaigns" element={<Navigate to="/outreach/campaigns" replace />} />

                    {/* F18 Phase C · Rendering Engine · admin UI. Lazy per route. */}
                    <Route
                        path="/admin/rendering"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/analytics"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingAnalytics />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/linking"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingLinking />
                            </Suspense>
                        }
                    />
                    {/* F18 Phase D · graph view + review queue + scheduled publishes */}
                    <Route
                        path="/admin/rendering/linking/graph"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <LinkingGraphPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/review"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingReviewQueue />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/schedules"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingSchedules />
                            </Suspense>
                        }
                    />
                    {/* F18 Phase E · translation matrix */}
                    <Route
                        path="/admin/rendering/translations"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingTranslations />
                            </Suspense>
                        }
                    />
                    {/* F20 Phase A · SEO Audit */}
                    <Route
                        path="/admin/seo/audit"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSeoAuditPage />
                            </Suspense>
                        }
                    />
                    {/* F20 Phase B · SEO Graph */}
                    <Route
                        path="/admin/seo/graph"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSeoGraphPage />
                            </Suspense>
                        }
                    />
                    {/* F20 Phase C · SEO Optimization (AI-first rewriter + snippets + schema) */}
                    <Route
                        path="/admin/seo/optimization"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSeoOptimizationPage />
                            </Suspense>
                        }
                    />
                    {/* F20 Phase D · SEO Monitoring dashboard */}
                    <Route
                        path="/admin/seo/monitoring"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSeoMonitoringPage />
                            </Suspense>
                        }
                    />
                    {/* F20 Phase D · SEO Indexing queue + history + settings */}
                    <Route
                        path="/admin/seo/indexing"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSeoIndexingPage />
                            </Suspense>
                        }
                    />
                    {/* F21 Phase A · Content Engine · entities list + detail */}
                    <Route
                        path="/admin/content/entities"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentEntitiesPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/content/entities/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentEntityDetailPage />
                            </Suspense>
                        }
                    />
                    {/* F21 Phase B · Content Generator (AI) */}
                    <Route
                        path="/admin/content/generator"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentGeneratorPage />
                            </Suspense>
                        }
                    />
                    {/* F21 Phase C · Content Schedule */}
                    <Route
                        path="/admin/content/schedule"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentSchedulePage />
                            </Suspense>
                        }
                    />
                    {/* F21 Phase D · Quality / Similarity / Priority queue */}
                    <Route
                        path="/admin/content/quality"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentQualityPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/content/similarity"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentSimilarityPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/content/priority-queue"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentPriorityQueuePage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/content/monitoring"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminContentMonitoringPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/semantic/graph"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminSemanticGraphPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/universe"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminUniverseDashboardPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/marketplace/f22"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <AdminMarketplaceF22Page />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/stories"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <AdminStoriesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/stories/quality-dashboard"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <StoryQualityDashboard />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/agents"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <AdminAgentsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/agents/tasks"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                    <AdminAgentTasksPanel />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/insights"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-black" />}>
                                    <AdminInsightsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/social"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminSocialPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/affiliates"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminAffiliatesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 10/14 · Lead Engine + Outreach Engine (Phase A). */}
                    <Route path="/admin/leads" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><LeadsListPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/outreach/sequences" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><OutreachSequencesAdminPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/outreach"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-black" />}>
                                    <AdminOutreachPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 10/14 · Phase B · operations pages */}
                    <Route
                        path="/admin/outreach/deliveries"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseBDeliveriesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/pipelines"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseBPipelinesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/deliverability"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseBDeliverabilityPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/events"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseBEventsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 10/14 · Phase C · scaling + monitoring */}
                    <Route
                        path="/admin/outreach/scaling"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseCScalingPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/monitoring"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseCMonitoringPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/anomalies"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseCAnomaliesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/brands"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseCBrandsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/tenants"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseDTenantsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/scheduler"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseDSchedulerPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/anomaly-ml"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseDAnomalyMlPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/alerts"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseDAlertsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ab-testing"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseDAbTestingPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-supervisor"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEAiSupervisorPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-decisions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEDecisionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-workflows"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEWorkflowsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-policies"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEPoliciesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-approvals"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEApprovalsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/outreach/ai-audit"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPhaseEAuditPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 11/14 · CRM Engine · Phase A — contacts/accounts/deals */}
                    <Route
                        path="/admin/crm/contacts"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmContactsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/accounts"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmAccountsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/deals"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmDealsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/admin/crm/pipeline" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><CRMPipelinePage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route path="/admin/crm/routing" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><CRMRoutingPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/crm/agents"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmAgentsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/funnel"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmFunnelPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/ai-decisions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmAiDecisionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/workflows"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmWorkflowsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/policies"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmPoliciesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/approvals"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmApprovalsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/audit"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmAuditPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/crm/supervisor"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminCrmSupervisorPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 12/14 · Press Engine · Phase A · 2 routes */}
                    <Route path="/admin/press/releases" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><PressReleasesListPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/press/release/:id"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressReleaseEditorPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 12/14 · Press Engine · Phase B · 2 routes */}
                    <Route path="/admin/press/generator" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><PressGeneratorPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/press/release/:id/preview"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressPreviewPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 12/14 · Press Engine · Phase C · 2 routes */}
                    <Route path="/admin/press/distribution" element={<ProtectedRoute adminOnly><Suspense fallback={<div className="min-h-screen bg-neutral-950" />}><V12AdminLayout><PressDistributionPage /></V12AdminLayout></Suspense></ProtectedRoute>} />
                    <Route
                        path="/admin/press/release/:id/distribution"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressReleaseDistributionPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/authority"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressAuthorityDashboard />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/release/:id/authority"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressReleaseAuthorityPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/supervisor"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressSupervisorPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/decisions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressDecisionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/policies"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressPoliciesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/approvals"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressApprovalsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/workflows"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressWorkflowsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/press/audit"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminPressSupervisorAuditPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 13/14 · Phase A · Distribution Engine (additive · zero-touch M12) */}
                    <Route
                        path="/admin/distribution/jobs"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionJobsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/channels"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionChannelsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 13/14 · Phase C · Distribution Engine (additive · zero-touch Phase A/B) */}
                    <Route
                        path="/admin/distribution/email"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionEmailPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/webhook"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionWebhookPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/rss"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionRSSPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/atom"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionAtomPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    {/* Motore 13/14 · Phase D · Wave D.4 · Director UI (additive · zero-touch) */}
                    <Route
                        path="/admin/distribution/authority"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionAuthorityPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/policies"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionPoliciesPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/supervisor"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionSupervisorPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/decisions"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionDecisionsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/approvals"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionApprovalsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/distribution/workflows"
                        element={
                            <ProtectedRoute adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                                    <AdminDistributionWorkflowsPage />
                                </Suspense>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/rendering/worlds/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingWorldDetail />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/topics/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingTopicDetail />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/clusters/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingClusterDetail />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/pages/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingPageEditor />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admin/rendering/long-tail/:id"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#0A0A0A]" />}>
                                <AdminRenderingLongTailDetail />
                            </Suspense>
                        }
                    />

                    {/* F18 Phase B · Rendering Engine · public dynamic content.
                        Lazy-loaded per route (keeps main bundle under 900 kB).
                        Each page handles its own 404 via ContentNotFoundPage. */}
                    <Route
                        path="/w/:worldSlug"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                <WorldPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/t/:worldSlug/:topicSlug"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                <TopicPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/c/:clusterSlug"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                <ClusterHubPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/p/:pageSlug"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                <ArticlePage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/q/:querySlug"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                <LongTailPage />
                            </Suspense>
                        }
                    />

                    {/* Phase II · Wave C.5a · Admin Blog Workbench. All routes
                        mounted under /admin/v9/* to stay zero-touch on the
                        legacy `/admin/blog` Phase 6A surface (AdminBlog.jsx).
                        Gated by EditorialRouteGuard which calls
                        /api/v9/blog/editorial-roles/me. Suspense fallback uses
                        the shared achromatic loading surface. */}
                    <Route path="/admin/v9/blog" element={<Navigate to="/admin/blog" replace />} />
                    <Route path="/admin/v9/blog/composer" element={<Navigate to="/admin/blog/composer" replace />} />
                    <Route path="/admin/v9/blog/composer/:postId" element={<Navigate to="/admin/blog/composer/:postId" replace />} />
                    <Route path="/admin/v9/blog/moderation" element={<Navigate to="/admin/blog/moderation" replace />} />
                    <Route
                        path="/admin/editorial-roles"
                        element={
                            <EditorialRouteGuard adminOnly>
                                <Suspense fallback={<div className="min-h-screen bg-[var(--color-bg)]" />}>
                                    <EditorialRolesManagerPage />
                                </Suspense>
                            </EditorialRouteGuard>
                        }
                    />

                    {/* Phase II · Wave C.5b · Admin Observability Dashboards.
                        4 pages mounted alongside the C.5a editorial core.
                        Same guard pattern — SitemapStats is admin-only. */}
                    <Route path="/admin/v9/blog/calendar" element={<Navigate to="/admin/blog/calendar" replace />} />
                    <Route path="/admin/v9/blog/clusters" element={<Navigate to="/admin/blog/clusters" replace />} />
                    <Route path="/admin/v9/seo/sitemap-stats" element={<Navigate to="/admin/seo/sitemap-stats" replace />} />
                    <Route path="/admin/v9/content-gen/jobs" element={<Navigate to="/admin/content-gen/jobs" replace />} />

                    {/* Vanity URL: /@username → /civilization/profile/{id}.
                        React Router 7 does not match a static prefix inside a
                        dynamic segment, so we route the whole 1st-segment
                        param and dispatch to UsernameRedirect only when it
                        starts with '@'. Everything else falls through to
                        NotFound. This route is LAST so more specific ones
                        (e.g. /pricing) win the ranking. */}
                    <Route path="/:vanity" element={<UsernameRedirect />} />

                    <Route path="*" element={<NotFound />} />
                </Routes>
                <ItalianNudge />
                <CookieBanner />
                <Suspense fallback={null}>
                    <V6CookieConsentBanner />
                </Suspense>
                <OnboardingWizard />
                {/* Hotfix 2026-10 · GlobalAIAssistant (floating chatbot
                    FAB bottom-right) rimosso su richiesta utente. Il
                    componente + AIAssistantFAB sono stati eliminati;
                    il backend /api/ai-assistant/* resta attivo per
                    eventuali reintegrazioni future. */}
                </AnalyticsTracker>
                <Toaster
                    theme="dark"
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "var(--color-card)",
                            border: "1px solid var(--color-border)",
                            color: "var(--color-text-main)",
                        },
                    }}
                />
                <AuthModal />
                </CurrencyProvider>
                </I18nProvider>
                </PhaseHSuperAdminProvider>
                </CartProvider>
                </AuthModalProvider>
            </AuthProvider>
            </LocaleRouteWrapper>
        </BrowserRouter>
    );
}

export default App;
